import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import type { Html5Qrcode as Html5QrcodeInstance } from "html5-qrcode";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Camera,
  Check,
  Clock3,
  Coffee,
  Download,
  History,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu as MenuIcon,
  Minus,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  UserRound,
  WalletCards,
  X,
  Zap,
} from "lucide-react";
import { bestsellers, menuItems } from "./data/menu";
import {
  edgeFunctionUrl,
  isSupabaseConfigured,
  sendMagicLink,
  sendPasswordReset,
  sendPhoneOtp,
  signInWithEmailPassword,
  supabase,
  updateAccountPassword,
  verifyPhoneOtp,
} from "./lib/supabase";
import { resolveMembershipUi, type MembershipCheck } from "./lib/membership-ui";
import { resolveJoinStep } from "./lib/join-flow";
import {
  emptyOtpGuard,
  formatOtpWait,
  getOtpGuardStatus,
  OTP_LOCK_MINUTES,
  recordOtpSend,
  type OtpGuardRecord,
} from "./lib/otp-guard";
import type { SubscriptionPlan } from "./types";

const WHATSAPP_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER || "918591942655";
const MENU_URL = "https://solo.petpooja.com/menu";
const OWNER_EMAIL = "mikyletalpade@gmail.com";
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  string | undefined;
const MAP_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4199.929277210862!2d72.82809571112553!3d18.994674554445677!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7cf81ecc8044b%3A0x4ae488740163b37c!2sSolo%20Cafe!5e1!3m2!1sen!2sin!4v1786061070215!5m2!1sen!2sin";
const MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=Solo%20Cafe%20Marathon%20Futurex%20Lower%20Parel";
const whatsappUrl = (message: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
const formatRedemptionGap = (minutes: number) => {
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${minutes} minutes`;
};
const formatIndiaDateTime = (value: string) =>
  new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(new Date(value));
const formatIndiaDateTimeInput = (value: string | number | Date) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(new Date(value))
    .reduce<Record<string, string>>((result, part) => {
      result[part.type] = part.value;
      return result;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};
const indiaDateTimeInputToIso = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}:00+05:30`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};
const surpriseAppetizers = menuItems.filter(
  (item) =>
    ["Garlic Bread & Dips", "Fries & More", "Momos"].includes(item.category) &&
    !["dips", "extra-momo", "schezwan"].includes(item.id),
);

type MostOrderedCount = {
  item_code: string;
  item_name: string;
  category: string;
  total_count: number;
  display_rank: number;
  active: boolean;
  updated_at: string;
};

function useMostOrderedCounts() {
  const [counts, setCounts] = useState<MostOrderedCount[]>([]);
  const loadCounts = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from("most_ordered_counts")
      .select(
        "item_code,item_name,category,total_count,display_rank,active,updated_at",
      )
      .eq("active", true)
      .order("display_rank");
    if (data) setCounts(data as MostOrderedCount[]);
  }, []);

  useEffect(() => {
    const initialLoad = window.setTimeout(() => void loadCounts(), 0);
    const interval = window.setInterval(loadCounts, 60_000);
    const refreshVisiblePage = () => {
      if (document.visibilityState === "visible") void loadCounts();
    };
    document.addEventListener("visibilitychange", refreshVisiblePage);
    return () => {
      window.clearTimeout(initialLoad);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshVisiblePage);
    };
  }, [loadCounts]);

  return counts;
}

function EngagementTracker() {
  const { pathname } = useLocation();
  const visitId = useRef("");
  const lastActivity = useRef(0);

  useEffect(() => {
    try {
      visitId.current =
        window.sessionStorage.getItem("solo-engagement-visit") ||
        crypto.randomUUID();
      window.sessionStorage.setItem("solo-engagement-visit", visitId.current);
    } catch {
      visitId.current = crypto.randomUUID();
    }
  }, []);

  useEffect(() => {
    if (!supabase || !visitId.current) return;
    if (pathname.startsWith("/admin") || pathname.startsWith("/staff")) return;
    const path =
      pathname
        .toLowerCase()
        .replace(/[^a-z0-9/_-]/g, "")
        .slice(0, 81) || "/";
    lastActivity.current = Date.now();
    const markActive = () => {
      lastActivity.current = Date.now();
    };
    const report = (activeSeconds: number) => {
      void supabase?.rpc("record_page_engagement", {
        p_path: path,
        p_visit_id: visitId.current,
        p_active_seconds: activeSeconds,
      });
    };
    report(0);
    const interval = window.setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastActivity.current < 60_000
      ) {
        report(15);
      }
    }, 15_000);
    window.addEventListener("pointerdown", markActive, { passive: true });
    window.addEventListener("keydown", markActive);
    window.addEventListener("scroll", markActive, { passive: true });
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("pointerdown", markActive);
      window.removeEventListener("keydown", markActive);
      window.removeEventListener("scroll", markActive);
    };
  }, [pathname]);

  return null;
}

function Logo({ light = false }: { light?: boolean }) {
  return (
    <NavLink
      to="/"
      className={`logo ${light ? "logo--light" : ""}`}
      aria-label="Solo Café home"
    >
      <span className="logo__crop" aria-hidden="true">
        <img className="logo__image" src="/brand/solo-cafe-logo.png" alt="" />
      </span>
    </NavLink>
  );
}

function Header({
  session,
  hasCurrentMembership,
  showJoinPrompt,
}: {
  session: Session | null;
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const links = [
    ["/membership", "Solo Club"],
    ["/solo-at-work", "Solo at Work"],
    ["/about", "Our story"],
    ["/visit", "Visit us"],
    ["/contact", "Contact"],
  ];
  async function signOut() {
    if (!supabase) return;
    setSigningOut(true);
    await supabase.auth.signOut({ scope: "local" });
    setSigningOut(false);
    setOpen(false);
  }
  return (
    <header className="site-header">
      <Logo />
      <button
        className="icon-button menu-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-label="Toggle navigation"
        aria-expanded={open}
      >
        {open ? <X /> : <MenuIcon />}
      </button>
      <nav
        className={`main-nav ${open ? "main-nav--open" : ""}`}
        aria-label="Primary navigation"
      >
        <a href={MENU_URL} target="_blank" rel="noreferrer">
          Menu <span aria-hidden="true">↗</span>
        </a>
        {links.map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setOpen(false)}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            {label}
          </NavLink>
        ))}
        {session ? (
          <>
            {hasCurrentMembership ? (
              <NavLink
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="button button--small button--ink"
              >
                <UserRound size={16} /> My pass
              </NavLink>
            ) : showJoinPrompt ? (
              <NavLink
                to="/join"
                onClick={() => setOpen(false)}
                className="button button--small button--ink"
              >
                <Coffee size={16} /> Join Coffee Pass
              </NavLink>
            ) : null}
            <button
              type="button"
              className="header-signout"
              onClick={signOut}
              disabled={signingOut}
            >
              <LogOut size={16} /> {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </>
        ) : showJoinPrompt ? (
          <>
            <NavLink
              to="/dashboard"
              onClick={() => setOpen(false)}
              className="button button--small button--outline"
            >
              <UserRound size={16} /> Log in
            </NavLink>
            <NavLink
              to="/join"
              onClick={() => setOpen(false)}
              className="button button--small button--ink"
            >
              <Coffee size={16} /> Sign up
            </NavLink>
          </>
        ) : null}
      </nav>
    </header>
  );
}

function Footer({
  hasCurrentMembership,
  showJoinPrompt,
  showDeskFuel = true,
}: {
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
  showDeskFuel?: boolean;
}) {
  return (
    <footer className="site-footer">
      {showDeskFuel && (
        <>
          <section
            className="footer__deskfuel"
            aria-labelledby="deskfuel-title"
          >
            <div className="footer__deskfuel-copy">
              <p className="eyebrow">Also from the Solo team</p>
              <h2 id="deskfuel-title">Meet Desk Fuel.</h2>
              <p>
                Solo Café also sets up vending machines for workplaces. That
                service is called <strong>Desk Fuel</strong>. We install and
                stock them with a wide variety of food, snacks and beverages, so
                your team always has something good close by.
              </p>
              <p className="footer__deskfuel-offer">
                <Building2 size={20} /> Ask us about a free vending machine for
                your office.
              </p>
              <a
                className="button button--gold"
                href="https://deskfuel.in"
                target="_blank"
                rel="noreferrer"
              >
                Explore Desk Fuel <ArrowRight size={18} />
              </a>
            </div>
            <a
              className="footer__deskfuel-image"
              href="https://deskfuel.in"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit Desk Fuel"
            >
              <img
                src="/brand/desk-fuel-vending-machine.jpg"
                alt="A Desk Fuel vending machine stocked with food, snacks and beverages in a modern office"
              />
            </a>
          </section>
          <section
            className="footer__feedback"
            aria-labelledby="feedback-title"
          >
            <div>
              <h2 id="feedback-title">
                Got feedback, gossip, complaints, ideas or compliments? Spill
                the tea.
              </h2>
              <p>It goes straight to the founders.</p>
            </div>
            <a href="mailto:spillthetea@m2llp.in">
              spillthetea@m2llp.in <ArrowRight size={20} />
            </a>
          </section>
        </>
      )}
      <div className="footer__top">
        <div>
          <Logo light />
          <p>
            Your everyday café inside Marathon Futurex, with a new home coming
            soon to Aurum Q Park.
          </p>
        </div>
        <div>
          <p className="eyebrow">Find us</p>
          <p>
            Marathon Futurex
            <br />
            Lower Parel, Mumbai
          </p>
          <p className="footer__coming-soon">
            Coming soon
            <br />
            Aurum Q Park, Navi Mumbai
          </p>
        </div>
        <div>
          <p className="eyebrow">Talk to us</p>
          <a href={`tel:+${WHATSAPP_NUMBER}`}>+91 85919 42655</a>
          <br />
          <a
            href={whatsappUrl("Hi Solo Café, I have a question.")}
            target="_blank"
            rel="noreferrer"
          >
            WhatsApp Solo
          </a>
          <br />
          <a href="mailto:partnerships@solocafe.in">partnerships@solocafe.in</a>
        </div>
        <div className="footer__links">
          <a href={MENU_URL} target="_blank" rel="noreferrer">
            Order from the menu ↗
          </a>
          {hasCurrentMembership ? (
            <NavLink to="/dashboard">Manage my Coffee Pass</NavLink>
          ) : showJoinPrompt ? (
            <NavLink to="/join">Join Coffee Pass</NavLink>
          ) : null}
          <NavLink to="/contact">Corporate & pop-up enquiries</NavLink>
          <NavLink to="/privacy">Privacy</NavLink>
          <NavLink to="/terms">Terms</NavLink>
          <NavLink to="/staff">Staff access</NavLink>
        </div>
      </div>
      <div className="footer__bottom">
        <span>© {new Date().getFullYear()} Solo Café</span>
        <a href="https://m2llp.in" target="_blank" rel="noreferrer">
          Made By M <span aria-hidden="true">↗</span>
        </a>
      </div>
    </footer>
  );
}

function MobileDock({
  hasCurrentMembership,
  showJoinPrompt,
}: {
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
}) {
  return (
    <div className="mobile-dock">
      <a href={MENU_URL} target="_blank" rel="noreferrer">
        <Search size={18} /> Menu
      </a>
      <a
        href={whatsappUrl("Hi Solo Café, I’d like to know more.")}
        target="_blank"
        rel="noreferrer"
      >
        <Phone size={18} /> WhatsApp
      </a>
      <NavLink to={hasCurrentMembership ? "/dashboard" : "/join"}>
        <Coffee size={18} />{" "}
        {hasCurrentMembership
          ? "My pass"
          : showJoinPrompt
            ? "Join pass"
            : "Coffee Pass"}
      </NavLink>
    </div>
  );
}

function Layout({
  children,
  session,
  hasCurrentMembership,
  showJoinPrompt,
}: {
  children: ReactNode;
  session: Session | null;
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
}) {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return (
    <>
      <Header
        session={session}
        hasCurrentMembership={hasCurrentMembership}
        showJoinPrompt={showJoinPrompt}
      />
      <main>{children}</main>
      <Footer
        hasCurrentMembership={hasCurrentMembership}
        showJoinPrompt={showJoinPrompt}
        showDeskFuel={
          !pathname.startsWith("/admin") && !pathname.startsWith("/staff")
        }
      />
      <MobileDock
        hasCurrentMembership={hasCurrentMembership}
        showJoinPrompt={showJoinPrompt}
      />
    </>
  );
}

function SectionHeading({
  eyebrow,
  title,
  copy,
}: {
  eyebrow: string;
  title: ReactNode;
  copy?: string;
}) {
  return (
    <div className="section-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

function Price({ value }: { value: number }) {
  return <span className="price">₹{value.toLocaleString("en-IN")}</span>;
}

function useSubscriptionPlans() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  useEffect(() => {
    if (!supabase) return;
    supabase
      .from("subscription_plans")
      .select("*,plan_drink_rules(drink_name,upgrade_allowed)")
      .eq("active", true)
      .order("sort_order")
      .then(({ data }) => {
        setPlans(
          (data || []).map((plan) => ({
            id: plan.id,
            name: plan.name,
            price: plan.price,
            coffeeCount: plan.coffee_count,
            validityDays: plan.validity_days,
            dailyLimit: plan.daily_limit,
            isUnlimited: Boolean(plan.is_unlimited),
            minimumRedemptionGapMinutes:
              plan.minimum_redemption_gap_minutes || 0,
            eligibleDrinks: plan.eligible_drinks || [],
            signatureUpgrades: (plan.plan_drink_rules || [])
              .filter(
                (rule: { upgrade_allowed: boolean }) => rule.upgrade_allowed,
              )
              .map((rule: { drink_name: string }) => rule.drink_name),
            upgradePrice: plan.upgrade_price,
            includes: plan.includes || [],
            excludes: plan.excludes || [],
            unusedCreditRule: plan.unused_credit_rule,
            active: plan.active,
          })),
        );
        setLoading(false);
      });
  }, []);
  return { plans, loading };
}

function PlanCards({
  plans,
  loading,
  hasCurrentMembership,
  showJoinPrompt,
}: {
  plans: SubscriptionPlan[];
  loading: boolean;
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
}) {
  if (loading) {
    return <div className="loading-block">Loading coffee plans…</div>;
  }
  if (!plans.length) {
    return (
      <div className="setup-notice">
        <div>
          <Coffee />
        </div>
        <h3>Coffee plans are being refreshed.</h3>
        <p>Please check back shortly or ask the Solo team at the counter.</p>
      </div>
    );
  }
  return (
    <div className="plan-grid">
      {plans.map((plan) => (
        <article
          className={`plan-card ${
            plan.name === "Solo Club 20" ? "plan-card--best" : ""
          } ${plan.isUnlimited ? "plan-card--premium" : ""}`}
          key={plan.id}
        >
          <div className="plan-card__topline">
            <p className="eyebrow">{plan.validityDays} days</p>
            {plan.name === "Solo Club 20" && (
              <span className="plan-tier-badge">Best Value</span>
            )}
            {plan.isUnlimited && (
              <span className="plan-tier-badge">Premium</span>
            )}
          </div>
          <h3>{plan.name}</h3>
          <Price value={plan.price} />
          <strong className="plan-card__headline">
            {plan.isUnlimited
              ? "For people who take their coffee seriously."
              : `${plan.coffeeCount} coffees`}
          </strong>
          {plan.isUnlimited ? (
            <p className="plan-card__promise">
              Unlimited coffee for 30 days.
              <strong>
                1 coffee at a time · Redeem again after{" "}
                {formatRedemptionGap(plan.minimumRedemptionGapMinutes)}
              </strong>
            </p>
          ) : (
            <p className="plan-card__promise">
              Maximum {plan.dailyLimit} coffees per day.
              {plan.name === "Solo Club 20" && (
                <strong>Works out to under ₹75 per coffee.</strong>
              )}
            </p>
          )}
          <ul className="plan-card__highlights">
            <li>
              <Check /> Valid for 30 days
            </li>
            <li>
              <Check /> Eight included coffee choices
            </li>
            <li>
              <Check /> Signature upgrade for ₹49
            </li>
          </ul>
          <div className="plan-card__actions">
            {hasCurrentMembership ? (
              <NavLink to="/dashboard" className="button button--red">
                Manage my current pass
              </NavLink>
            ) : showJoinPrompt ? (
              <NavLink
                to={`/join?plan=${plan.id}`}
                className={`button ${plan.isUnlimited ? "button--cream" : "button--red"}`}
              >
                Choose {plan.name}
              </NavLink>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}

function HomePage({
  hasCurrentMembership,
  showJoinPrompt,
}: {
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
}) {
  const { plans, loading: plansLoading } = useSubscriptionPlans();
  const mostOrderedCounts = useMostOrderedCounts();
  const countByItem = new Map(
    mostOrderedCounts.map((item) => [item.item_code, item.total_count]),
  );
  const featuredOrders = bestsellers.slice(0, 5).map((item) => ({
    ...item,
    count: countByItem.get(item.id) || 0,
  }));
  return (
    <>
      <section className="hero">
        <div className="hero__copy reveal">
          <h1>
            Your workday,
            <br />
            <em>better brewed.</em>
          </h1>
          <p className="hero__lede">
            Specialty coffee, craveable comfort food and quick service, made for
            the break you actually have.
          </p>
          <div className="button-row">
            <a
              href={MENU_URL}
              target="_blank"
              rel="noreferrer"
              className="button button--red"
            >
              View the live menu <ArrowRight size={18} />
            </a>
            {hasCurrentMembership ? (
              <NavLink to="/dashboard" className="button button--outline">
                Open my Coffee Pass
              </NavLink>
            ) : showJoinPrompt ? (
              <NavLink to="/join" className="button button--outline">
                Join the Coffee Club
              </NavLink>
            ) : null}
          </div>
          <div className="hero__proof">
            <div>
              <strong>Fast</strong>
              <span>for busy breaks</span>
            </div>
            <div>
              <strong>Fresh</strong>
              <span>made at the café</span>
            </div>
            <div>
              <strong>Right here</strong>
              <span>at Futurex</span>
            </div>
          </div>
        </div>
        <div
          className="hero__visual reveal reveal--delay"
          aria-label="Solo Café cold coffees"
        >
          <div className="hero__stamp">
            BREAK
            <br />
            BETTER
          </div>
          <div className="hero__image-wrap">
            <img
              src="/media/solo-cafe-feast.jpg"
              alt="Solo Café pizzas, pasta, iced coffee and choco lava cake"
            />
          </div>
          <span className="hero__star">✦</span>
        </div>
      </section>

      <div className="ticker" aria-hidden="true">
        <div>
          {[
            "SPECIALTY COFFEE",
            "PIZZAS",
            "PASTAS",
            "MOMOS",
            "SANDWICHES",
            "DESSERTS",
            "SPECIALTY COFFEE",
            "PIZZAS",
          ].map((item, i) => (
            <span key={i}>
              {item} <b>✦</b>
            </span>
          ))}
        </div>
      </div>

      <section
        className="aurum-coming-soon"
        aria-labelledby="aurum-coming-soon-title"
      >
        <div className="aurum-coming-soon__header">
          <h2 id="aurum-coming-soon-title">
            Solo Café is coming to <em>Navi Mumbai.</em>
          </h2>
          <p>
            A new Solo is taking shape at Aurum Q Park. Follow the road from
            Lower Parel to our next coffee stop.
          </p>
        </div>
        <figure className="aurum-journey">
          <img
            src="/media/solo-marathon-to-aurum-journey-love.webp"
            alt="An architectural illustration of Marathon Futurex and Aurum Q Park connected by a road, with a Solo Café coffee van travelling toward Navi Mumbai"
            width={1800}
            height={600}
            loading="eager"
            decoding="async"
          />
          <figcaption>
            <span className="aurum-journey__place">
              <strong>Marathon Futurex</strong>
              <small>Lower Parel</small>
            </span>
            <span className="aurum-journey__track" aria-hidden="true">
              <span className="aurum-journey__line" />
              <span className="aurum-journey__cup">
                <Coffee size={25} />
              </span>
            </span>
            <span className="aurum-journey__place aurum-journey__place--end">
              <strong>Aurum Q Park</strong>
              <small>Navi Mumbai · Opening soon</small>
            </span>
          </figcaption>
        </figure>
      </section>

      <section className="intro split-section">
        <div className="intro__art">
          <span className="big-type">SOLO</span>
          <div className="art-card">
            <img
              src="/media/solo-iced-coffee.jpg"
              alt="A chilled Solo Café iced coffee"
            />
          </div>
        </div>
        <div className="intro__copy">
          <p className="eyebrow">Your everyday café</p>
          <h2>
            A proper café break. <em>Without leaving the building.</em>
          </h2>
          <p>
            Solo Café brings together speciality coffee and bold, familiar food
            in one colourful corner of Marathon Futurex. Drop in between
            meetings, grab a quick desk lunch, or reset over an iced coffee.
          </p>
          <div className="mini-facts">
            <span>
              <Clock3 /> Built for short breaks
            </span>
            <span>
              <Zap /> Quick counter service
            </span>
            <span>
              <MapPin /> Lower Parel
            </span>
          </div>
          <NavLink to="/about" className="text-link">
            Meet Solo <ArrowRight size={17} />
          </NavLink>
        </div>
      </section>

      <section className="bestsellers section-pad">
        <SectionHeading
          eyebrow="The office favourites"
          title={
            <>
              Big cravings.
              <br />
              <em>Zero guesswork.</em>
            </>
          }
          copy="The current most-ordered picks, straight from Solo Café’s latest menu."
        />
        <div className="order-ticker" aria-label="Live most-ordered counts">
          <div className="order-ticker__track">
            {[...featuredOrders, ...featuredOrders].map((item, index) => (
              <span
                key={`${item.id}-${index}`}
                aria-hidden={index >= featuredOrders.length}
              >
                Over
                <strong>{item.count.toLocaleString("en-IN")}</strong>
                {item.name}
                <b className="order-ticker__sold">Sold</b>
                <i aria-hidden="true">✦</i>
              </span>
            ))}
          </div>
        </div>
        <div className="bestseller-grid">
          {featuredOrders.map((item, index) => (
            <article
              className={`bestseller-card card-${index + 1}`}
              key={item.id}
            >
              <span className="rank">0{index + 1}</span>
              <div>
                <p className="eyebrow">{item.category}</p>
                <h3>{item.name}</h3>
                {item.description && <p>{item.description}</p>}
                <span className="order-count">
                  Over <strong>{item.count.toLocaleString("en-IN")}</strong>{" "}
                  Sold
                </span>
                <Price value={item.price} />
              </div>
            </article>
          ))}
        </div>
        <a
          href={MENU_URL}
          target="_blank"
          rel="noreferrer"
          className="button button--ink centered"
        >
          See the live menu <ArrowRight size={18} />
        </a>
      </section>

      <section className="club-teaser">
        <div className="club-teaser__copy">
          <p className="eyebrow eyebrow--gold">The Solo Coffee Club</p>
          <h2>
            One less decision
            <br />
            before 10 AM.
          </h2>
          <p>
            Pre-load your coffee routine, keep your balance in one place, and
            redeem securely at the café.
          </p>
          <ul>
            <li>
              <Check /> A live coffee balance
            </li>
            <li>
              <Check /> Quick QR redemption
            </li>
            <li>
              <Check /> Eligible-coffee upgrades for ₹49
            </li>
          </ul>
          <NavLink to="/membership" className="button button--cream">
            How the club works <ArrowRight size={18} />
          </NavLink>
        </div>
        <div className="pass-card">
          <div className="pass-card__top">
            <Logo light />
            <Coffee />
          </div>
          <p>COFFEE CLUB</p>
          <div className="pass-card__ring">
            <span>your</span>
            <strong>daily</strong>
            <span>ritual</span>
          </div>
          <small>Marathon Futurex · Lower Parel</small>
        </div>
      </section>

      <section className="home-plans section-pad">
        <SectionHeading
          eyebrow="Solo Coffee Club plans"
          title={
            <>
              Your month,
              <br />
              <em>three ways.</em>
            </>
          }
          copy="Choose 12 coffees, 20 coffees, or Unlimited. Every plan includes eight everyday coffee choices."
        />
        <PlanCards
          plans={plans}
          loading={plansLoading}
          hasCurrentMembership={hasCurrentMembership}
          showJoinPrompt={showJoinPrompt}
        />
        <NavLink to="/membership" className="text-link home-plans__details">
          See included coffees and full plan rules <ArrowRight size={17} />
        </NavLink>
      </section>

      <section className="break-flow section-pad">
        <SectionHeading
          eyebrow="Made for the workday"
          title={
            <>
              From “quick break?”
              <br />
              to <em>back at your desk.</em>
            </>
          }
        />
        <div className="steps">
          <article>
            <span>01</span>
            <Coffee />
            <h3>Pick your fix</h3>
            <p>Coffee, pizza, pasta or a quick bite from the current menu.</p>
          </article>
          <article>
            <span>02</span>
            <Zap />
            <h3>Order at Solo</h3>
            <p>Visit the café counter inside Marathon Futurex.</p>
          </article>
          <article>
            <span>03</span>
            <Clock3 />
            <h3>Take your break back</h3>
            <p>Fast service designed around the pace of your office day.</p>
          </article>
        </div>
      </section>

      <section className="workplace-teaser">
        <div className="workplace-teaser__copy">
          <p className="eyebrow eyebrow--gold">Bring Solo to work</p>
          <h2>
            Your workplace could use
            <br />
            <em>a better coffee break.</em>
          </h2>
          <p>
            Invite Solo Café to become part of your corporate park, or bring a
            focused coffee-and-food pop-up into your office for a day, event or
            employee programme.
          </p>
          <NavLink to="/contact" className="button button--cream">
            Talk to us <ArrowRight size={18} />
          </NavLink>
        </div>
        <div className="workplace-options">
          <article>
            <Building2 />
            <p className="eyebrow eyebrow--gold">Long-term</p>
            <h3>Corporate park café</h3>
            <p>A resident Solo Café built around the pace of your workplace.</p>
          </article>
          <article>
            <Store />
            <p className="eyebrow eyebrow--gold">Flexible</p>
            <h3>Office pop-up</h3>
            <p>A compact Solo experience for team days, events and launches.</p>
          </article>
        </div>
      </section>

      <section className="visit-strip">
        <div>
          <p className="eyebrow">See you downstairs</p>
          <h2>Your next coffee is closer than you think.</h2>
        </div>
        <NavLink to="/visit" className="button button--outline-light">
          Find Solo Café <ArrowRight size={18} />
        </NavLink>
      </section>
    </>
  );
}

function MenuPage() {
  return (
    <div className="page page--menu">
      <section className="official-menu-hero">
        <div className="official-menu-hero__copy reveal">
          <p className="eyebrow">The live Solo menu</p>
          <h1>
            Pick your
            <br />
            <em>next break.</em>
          </h1>
          <p>
            Browse current dishes, prices and availability on Solo Café’s
            official Petpooja menu.
          </p>
          <a
            className="button button--red"
            href={MENU_URL}
            target="_blank"
            rel="noreferrer"
          >
            Open the live menu <ArrowRight size={18} />
          </a>
        </div>
        <div className="official-menu-hero__image reveal reveal--delay">
          <img
            src="/media/solo-cafe-feast.jpg"
            alt="Solo Café pizza, pasta, iced coffee and choco lava cake"
          />
        </div>
      </section>
      <section className="menu-photo-grid" aria-label="Solo Café favourites">
        <figure className="menu-photo menu-photo--wide">
          <img
            src="/media/solo-arrabbiata-pasta.jpg"
            alt="A bowl of Solo Café arrabbiata pasta"
          />
          <figcaption>
            <span>Comfort in a bowl</span>
            <strong>Pastas with a little fire.</strong>
          </figcaption>
        </figure>
        <figure className="menu-photo menu-photo--portrait">
          <img
            src="/media/solo-iced-coffee.jpg"
            alt="A refreshing Solo Café iced coffee"
          />
          <figcaption>
            <span>Workday essential</span>
            <strong>Cold coffee, properly chilled.</strong>
          </figcaption>
        </figure>
        <div className="menu-photo-cta">
          <p className="eyebrow eyebrow--gold">Hungry already?</p>
          <h2>See what’s available right now.</h2>
          <p>
            The Petpooja menu is the source for today’s selection and pricing.
          </p>
          <a
            className="button button--cream"
            href={MENU_URL}
            target="_blank"
            rel="noreferrer"
          >
            View menu <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}

function MembershipPage({
  hasCurrentMembership,
  showJoinPrompt,
}: {
  hasCurrentMembership: boolean;
  showJoinPrompt: boolean;
}) {
  const { plans, loading } = useSubscriptionPlans();
  const includedCoffees = [
    "Espresso",
    "Americano",
    "Cappuccino",
    "Latte",
    "Flat White",
    "Iced Americano",
    "Iced Latte",
    "Cold Brew",
  ];
  const signatureCoffees = [
    "Café Mocha",
    "House Cold Coffee",
    "Iced Mocha",
    "Vietnamese Cold Brew",
    "Fridge Ciggy",
    "Sunrise Americano",
    "Coco-cano",
  ];
  return (
    <div className="page membership-page">
      <section className="page-hero page-hero--club">
        <p className="eyebrow eyebrow--gold">Solo Coffee Club</p>
        <h1>
          Your coffee routine,
          <br />
          <em>already sorted.</em>
        </h1>
        <p>
          Choose your plan, pay at the Solo counter, and keep every redemption
          in your pocket.
        </p>
        <NavLink to="/dashboard" className="button button--cream">
          Open my coffee pass <ArrowRight size={18} />
        </NavLink>
      </section>
      <section className="how-club section-pad">
        <SectionHeading
          eyebrow="Simple by design"
          title={
            <>
              Join once.
              <br />
              <em>Brew through the month.</em>
            </>
          }
        />
        <div className="steps steps--light">
          <article>
            <span>01</span>
            <UserRound />
            <h3>Choose & sign in</h3>
            <p>Select a plan and sign in securely with the OTP sent by SMS.</p>
          </article>
          <article>
            <span>02</span>
            <WalletCards />
            <h3>Pay at the counter</h3>
            <p>
              Solo staff records your offline payment and activates the correct
              plan.
            </p>
          </article>
          <article>
            <span>03</span>
            <BadgeCheck />
            <h3>Scan & sip</h3>
            <p>
              Open your short-lived QR. Staff confirms the drink and your
              balance updates instantly.
            </p>
          </article>
        </div>
      </section>
      <section className="plans section-pad">
        <SectionHeading
          eyebrow="Coffee plans"
          title={
            <>
              Pick your
              <br />
              <em>workday rhythm.</em>
            </>
          }
        />
        <PlanCards
          plans={plans}
          loading={loading}
          hasCurrentMembership={hasCurrentMembership}
          showJoinPrompt={showJoinPrompt}
        />
        {!loading && plans.length > 0 && (
          <div className="club-rules">
            <article className="club-rule-card club-rule-card--wide">
              <Coffee />
              <div>
                <p className="eyebrow">Included in every plan</p>
                <h3>Your everyday coffee line-up.</h3>
                <p>{includedCoffees.join(" · ")}</p>
              </div>
            </article>
            <article className="club-rule-card">
              <Sparkles />
              <div>
                <p className="eyebrow">₹49 Signature upgrade</p>
                <h3>Make any redemption a little extra.</h3>
                <p>{signatureCoffees.join(" · ")}</p>
                <small>₹49 is charged each time an upgrade is selected.</small>
              </div>
            </article>
            <article className="club-rule-card">
              <Minus />
              <div>
                <p className="eyebrow">Not included</p>
                <h3>Chocolate drinks and add-ons.</h3>
                <p>
                  Hot Chocolate, Cold Chocolate and Solo Special Hot Chocolate
                  are not included in any plan.
                </p>
                <small>
                  Flavours, oat milk, almond milk, coconut milk and other
                  alternative milks are charged at the regular menu price.
                </small>
              </div>
            </article>
            <article className="club-rule-card club-rule-card--wide club-rule-card--plain">
              <ShieldCheck />
              <div>
                <p className="eyebrow">Rules that matter</p>
                <h3>Simple, personal and valid for 30 days.</h3>
                <ul>
                  <li>Solo Club 12 and 20 allow three redemptions per day.</li>
                  <li>Solo Unlimited can be used again after three hours.</li>
                  <li>Each redemption covers one drink only.</li>
                  <li>Memberships cannot be shared or transferred.</li>
                  <li>The member must be present when redeeming.</li>
                  <li>Unused credits expire with no rollover or cash value.</li>
                </ul>
              </div>
            </article>
          </div>
        )}
      </section>
      <section className="security-note">
        <ShieldCheck />
        <div>
          <h3>Secure at every scan.</h3>
          <p>
            Your QR contains no phone number or personal data. It expires
            quickly, rejects repeat use, and only deducts a coffee after staff
            confirmation.
          </p>
        </div>
      </section>
    </div>
  );
}

function SignInPanel({
  title = "Open your Solo pass",
  method = "phone",
  redirectPath = "/dashboard",
  allowSignup = true,
  allowPassword = false,
}: {
  title?: string;
  method?: "phone" | "email";
  redirectPath?: string;
  allowSignup?: boolean;
  allowPassword?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailAuthMode, setEmailAuthMode] = useState<"password" | "link">(
    allowPassword ? "password" : "link",
  );
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [guardNow, setGuardNow] = useState(0);
  const [otpGuard, setOtpGuard] = useState<OtpGuardRecord>(() => {
    if (typeof window === "undefined") return emptyOtpGuard();
    try {
      return JSON.parse(
        window.localStorage.getItem("solo-otp-send-guard") || "null",
      ) as OtpGuardRecord;
    } catch {
      return emptyOtpGuard();
    }
  });
  const normalizedPhone = `+91${phone.replace(/\D/g, "")}`;
  const otpGuardStatus = getOtpGuardStatus(
    otpGuard,
    guardNow || Number.MAX_SAFE_INTEGER,
  );
  const captchaReady = !TURNSTILE_SITE_KEY || Boolean(captchaToken);
  const captchaChallenge = TURNSTILE_SITE_KEY ? (
    <div className="turnstile-check">
      <Turnstile
        ref={turnstileRef}
        siteKey={TURNSTILE_SITE_KEY}
        onSuccess={(token) => {
          setCaptchaToken(token);
          setError("");
        }}
        onExpire={() => setCaptchaToken("")}
        onError={() => {
          setCaptchaToken("");
          setError(
            "The security check could not load. Please refresh and try again.",
          );
        }}
        options={{ theme: "light", size: "flexible" }}
      />
      <span>
        <ShieldCheck size={15} /> Protected by Cloudflare Turnstile
      </span>
    </div>
  ) : null;
  useEffect(() => {
    const timer = window.setTimeout(() => setGuardNow(Date.now()), 0);
    return () => window.clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (method !== "phone" || otpGuardStatus.allowed) return;
    const timer = window.setInterval(() => setGuardNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [method, otpGuardStatus.allowed]);
  function guardOtpRequest() {
    const status = getOtpGuardStatus(otpGuard, Date.now());
    setGuardNow(Date.now());
    if (status.allowed) return true;
    setError(
      status.reason === "locked"
        ? `For your security, OTP sending is paused for ${formatOtpWait(status.waitSeconds)} after three requests.`
        : `Please wait ${formatOtpWait(status.waitSeconds)} before requesting another OTP.`,
    );
    return false;
  }
  function saveOtpSend() {
    const next = recordOtpSend(otpGuard, Date.now());
    setOtpGuard(next);
    setGuardNow(Date.now());
    window.localStorage.setItem("solo-otp-send-guard", JSON.stringify(next));
  }
  async function emailLogin(event: FormEvent) {
    event.preventDefault();
    try {
      setError("");
      setLoading(true);
      const { error: authError } = await sendMagicLink(
        email.trim(),
        redirectPath,
        method !== "email",
        captchaToken || undefined,
      );
      if (authError) throw authError;
      setCaptchaToken("");
      turnstileRef.current?.reset();
      setSent(true);
    } catch (e) {
      if (TURNSTILE_SITE_KEY) {
        setCaptchaToken("");
        turnstileRef.current?.reset();
      }
      setError(
        e instanceof Error ? e.message : "Could not send the sign-in link.",
      );
    } finally {
      setLoading(false);
    }
  }
  async function passwordLogin(event: FormEvent) {
    event.preventDefault();
    try {
      setError("");
      setLoading(true);
      const { error: authError } = await signInWithEmailPassword(
        email.trim(),
        password,
        captchaToken || undefined,
      );
      if (authError) {
        throw new Error(
          "The email or password is incorrect. If this is your first password login, set your password below.",
        );
      }
      setCaptchaToken("");
      turnstileRef.current?.reset();
    } catch (e) {
      if (TURNSTILE_SITE_KEY) {
        setCaptchaToken("");
        turnstileRef.current?.reset();
      }
      setError(e instanceof Error ? e.message : "Could not sign in.");
    } finally {
      setLoading(false);
    }
  }
  async function requestPasswordReset() {
    if (!email.trim()) {
      setError("Enter your approved staff email first.");
      return;
    }
    try {
      setError("");
      setLoading(true);
      const { error: authError } = await sendPasswordReset(
        email.trim(),
        `${redirectPath}?reset-password=1`,
        captchaToken || undefined,
      );
      if (authError) throw authError;
      setPasswordResetSent(true);
      setCaptchaToken("");
      turnstileRef.current?.reset();
    } catch (e) {
      if (TURNSTILE_SITE_KEY) {
        setCaptchaToken("");
        turnstileRef.current?.reset();
      }
      setError(
        e instanceof Error ? e.message : "Could not send the password link.",
      );
    } finally {
      setLoading(false);
    }
  }
  async function requestOtp(event: FormEvent) {
    event.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (!/^[6-9][0-9]{9}$/.test(digits))
      return setError("Enter a valid 10-digit Indian mobile number.");
    if (!guardOtpRequest()) return;
    try {
      setError("");
      setLoading(true);
      const fullPhone = `+91${digits}`;
      const { error: authError } = await sendPhoneOtp(
        fullPhone,
        captchaToken || undefined,
        allowSignup,
      );
      if (authError) throw authError;
      saveOtpSend();
      setCaptchaToken("");
      turnstileRef.current?.reset();
      setSent(true);
    } catch (e) {
      if (TURNSTILE_SITE_KEY) {
        setCaptchaToken("");
        turnstileRef.current?.reset();
      }
      setError(e instanceof Error ? e.message : "Could not send the OTP.");
    } finally {
      setLoading(false);
    }
  }
  async function confirmOtp(event: FormEvent) {
    event.preventDefault();
    if (!/^[0-9]{6}$/.test(otp)) return setError("Enter the 6-digit OTP.");
    try {
      setError("");
      setLoading(true);
      const { error: authError } = await verifyPhoneOtp(normalizedPhone, otp);
      if (authError) throw authError;
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "The OTP could not be verified.",
      );
    } finally {
      setLoading(false);
    }
  }
  if (method === "email")
    return (
      <div className="auth-panel">
        <div className="auth-panel__icon">
          <Coffee />
        </div>
        <p className="eyebrow">Secure staff sign-in</p>
        <h2>{title}</h2>
        {passwordResetSent ? (
          <div className="magic-link-sent">
            <BadgeCheck />
            <h3>Check your inbox</h3>
            <p>
              If an account exists for <strong>{email}</strong>, we sent a
              secure password recovery email.
            </p>
            <p className="form-note">
              Open the secure link in the email. It will bring you back to
              solocafe.in, where you can choose your new password.
            </p>
            <button
              className="text-link magic-reset"
              onClick={() => {
                setPasswordResetSent(false);
                setError("");
              }}
            >
              Return to login
            </button>
          </div>
        ) : sent ? (
          <div className="magic-link-sent">
            <BadgeCheck />
            <h3>Check your inbox</h3>
            <p>
              We sent a one-time sign-in link to <strong>{email}</strong>. Open
              it on this device to continue.
            </p>
            <button
              className="text-link magic-reset"
              onClick={() => setSent(false)}
            >
              Use another email
            </button>
          </div>
        ) : (
          <>
            {allowPassword && (
              <div
                className="auth-method-switch"
                role="tablist"
                aria-label="Account sign-in method"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={emailAuthMode === "password"}
                  className={emailAuthMode === "password" ? "active" : ""}
                  onClick={() => {
                    setEmailAuthMode("password");
                    setError("");
                  }}
                >
                  Password
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={emailAuthMode === "link"}
                  className={emailAuthMode === "link" ? "active" : ""}
                  onClick={() => {
                    setEmailAuthMode("link");
                    setError("");
                  }}
                >
                  Email link
                </button>
              </div>
            )}
            {allowPassword && emailAuthMode === "password" ? (
              <>
                <p>
                  Sign in with the email and password connected to your approved
                  staff account.
                </p>
                <form onSubmit={passwordLogin} className="magic-link-form">
                  <label>
                    Email address
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      required
                    />
                  </label>
                  <label>
                    Password
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      minLength={8}
                      required
                    />
                  </label>
                  {captchaChallenge}
                  <button
                    className="button button--red"
                    type="submit"
                    disabled={
                      !isSupabaseConfigured ||
                      loading ||
                      password.length < 8 ||
                      !captchaReady
                    }
                  >
                    {loading ? (
                      "Signing in…"
                    ) : (
                      <>
                        Sign in to staff <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
                <button
                  className="text-link password-reset-action"
                  type="button"
                  onClick={requestPasswordReset}
                  disabled={loading || !captchaReady}
                >
                  Forgot password or set one
                </button>
              </>
            ) : (
              <>
                <p>
                  Enter your approved staff email and we’ll send a secure
                  one-time link.
                </p>
                <form onSubmit={emailLogin} className="magic-link-form">
                  <label>
                    Email address
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      required
                    />
                  </label>
                  {captchaChallenge}
                  <button
                    className="button button--red"
                    type="submit"
                    disabled={!isSupabaseConfigured || loading || !captchaReady}
                  >
                    {loading ? (
                      "Sending…"
                    ) : (
                      <>
                        Email me a sign-in link <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
            {!isSupabaseConfigured && (
              <p className="form-note">
                Sign-in will activate when the café’s Supabase keys are
                connected.
              </p>
            )}
          </>
        )}
        {error && <p className="error-text">{error}</p>}
        <p className="fine-print">
          Staff and administrator access is granted separately by role. A valid
          sign-in alone does not grant operational access.
        </p>
      </div>
    );
  return (
    <div className="auth-panel">
      <div className="auth-panel__icon">
        <Phone />
      </div>
      <p className="eyebrow">Mobile member sign-in</p>
      <h2>{title}</h2>
      {sent ? (
        <>
          <p>
            Check your Messages for the six-digit SMS OTP sent to{" "}
            <strong>{normalizedPhone}</strong>.
          </p>
          <form onSubmit={confirmOtp} className="magic-link-form">
            <label>
              One-time password
              <input
                className="otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(event) =>
                  setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                minLength={6}
                maxLength={6}
                required
              />
            </label>
            <button
              className="button button--red"
              type="submit"
              disabled={!isSupabaseConfigured || loading || otp.length !== 6}
            >
              {loading ? (
                "Verifying…"
              ) : (
                <>
                  Verify and continue <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
          <button
            className="text-link magic-reset"
            onClick={() => {
              setSent(false);
              setOtp("");
              setError("");
              setCaptchaToken("");
              turnstileRef.current?.reset();
            }}
          >
            Change mobile number
          </button>
        </>
      ) : (
        <>
          <p>
            {allowSignup
              ? "Use your Indian mobile number, then check your Messages for the SMS OTP."
              : "Enter the mobile number you used to sign up, then check your Messages for the SMS login OTP."}
          </p>
          <form onSubmit={requestOtp} className="magic-link-form">
            <label>
              Mobile number
              <div className="phone-input">
                <span>+91</span>
                <input
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phone}
                  onChange={(event) =>
                    setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  placeholder="98765 43210"
                  minLength={10}
                  maxLength={10}
                  required
                />
              </div>
            </label>
            {captchaChallenge}
            <button
              className="button button--red"
              type="submit"
              disabled={
                !isSupabaseConfigured ||
                loading ||
                !otpGuardStatus.allowed ||
                !captchaReady
              }
            >
              {loading ? (
                "Sending…"
              ) : !otpGuardStatus.allowed ? (
                `Try again in ${formatOtpWait(otpGuardStatus.waitSeconds)}`
              ) : (
                <>
                  {allowSignup ? "Send SMS OTP" : "Send login OTP by SMS"}{" "}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
          <p className="otp-security-note">
            <ShieldCheck size={16} /> Maximum three OTP sends, followed by a{" "}
            {OTP_LOCK_MINUTES}-minute security pause.
          </p>
          {!isSupabaseConfigured && (
            <p className="form-note">
              Sign-in will activate when the café’s Supabase keys are connected.
            </p>
          )}
        </>
      )}
      {error && <p className="error-text">{error}</p>}
      <p className="fine-print">
        OTP verification confirms your number. Your Coffee Pass activates only
        after Solo staff verifies payment. By continuing, you agree to the terms
        and privacy policy.
      </p>
    </div>
  );
}

function embeddedOne<T>(value: T | T[] | null | undefined): T | undefined {
  return Array.isArray(value) ? value[0] : (value ?? undefined);
}

type MembershipView = {
  id: string;
  status: string;
  starts_at: string | null;
  expires_at: string | null;
  membership_sequence: number;
  returning_member: boolean;
  subscription_plans: {
    name: string;
    eligible_drinks?: string[];
    is_unlimited: boolean;
    minimum_redemption_gap_minutes: number;
  };
  membership_balances:
    | { purchased: number; used: number; remaining: number }
    | Array<{ purchased: number; used: number; remaining: number }>;
  membership_surprises:
    | {
        appetizer_name: string;
        unlock_redemption_number: number;
        status: "scheduled" | "claimed";
        claimed_at: string | null;
      }
    | Array<{
        appetizer_name: string;
        unlock_redemption_number: number;
        status: "scheduled" | "claimed";
        claimed_at: string | null;
      }>;
};

type MemberSection = "pass" | "history" | "payments";

type MemberRedemption = {
  id: string;
  drink_name: string;
  upgrade_amount: number;
  created_at: string;
  reversed_at: string | null;
  reversal_reason: string | null;
  source: string;
};

type MemberPayment = {
  id: string;
  amount: number;
  kind: string;
  status: string;
  created_at: string;
};

function MemberDashboard({ session }: { session: Session | null }) {
  const [membership, setMembership] = useState<MembershipView | null>(null);
  const [queuedMembership, setQueuedMembership] =
    useState<MembershipView | null>(null);
  const [memberSection, setMemberSection] = useState<MemberSection>("pass");
  const [qrToken, setQrToken] = useState("");
  const [qrError, setQrError] = useState("");
  const [redemptions, setRedemptions] = useState<MemberRedemption[]>([]);
  const [payments, setPayments] = useState<MemberPayment[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [loading, setLoading] = useState(Boolean(session));
  useEffect(() => {
    if (!supabase || !session) return;
    const client = supabase;
    void client.rpc("refresh_own_membership").then(() =>
      client
        .from("memberships")
        .select(
          "id,status,starts_at,expires_at,membership_sequence,returning_member,subscription_plans(name,eligible_drinks,is_unlimited,minimum_redemption_gap_minutes),membership_balances(purchased,used,remaining),membership_surprises(appetizer_name,unlock_redemption_number,status,claimed_at)",
        )
        .eq("profile_id", session.user.id)
        .in("status", ["active", "pending", "paused"])
        .order("created_at", { ascending: false })
        .limit(5)
        .then(({ data }) => {
          const rows = (data || []) as unknown as MembershipView[];
          const queued =
            rows.find(
              (row) => row.status === "pending" && Boolean(row.starts_at),
            ) || null;
          const current =
            rows.find((row) => ["active", "paused"].includes(row.status)) ||
            rows.find((row) => row.status === "pending" && !row.starts_at) ||
            queued;
          if (current) {
            setActivityLoading(true);
            setActivityError("");
          }
          setQueuedMembership(current?.id === queued?.id ? null : queued);
          setMembership(current || null);
          setLoading(false);
        }),
    );
  }, [session]);
  useEffect(() => {
    if (!membership || membership.status !== "active") return;
    const load = async () => {
      try {
        const response = await fetch(edgeFunctionUrl("qr-token"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ membershipId: membership.id }),
        });
        const data = (await response.json()) as {
          token?: string;
          message?: string;
        };
        if (!response.ok || !data.token)
          throw new Error(data.message || "Could not refresh the secure QR.");
        setQrToken(data.token);
        setQrError("");
      } catch (error) {
        setQrToken("");
        setQrError(
          error instanceof Error
            ? error.message
            : "Could not refresh the secure QR.",
        );
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 45000);
    return () => clearInterval(timer);
  }, [membership, session]);
  useEffect(() => {
    if (!supabase || !membership) return;
    Promise.all([
      supabase
        .from("redemptions")
        .select(
          "id,drink_name,upgrade_amount,created_at,reversed_at,reversal_reason,source",
        )
        .eq("membership_id", membership.id)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("payments")
        .select("id,amount,kind,status,created_at")
        .eq("profile_id", session?.user.id || "")
        .order("created_at", { ascending: false })
        .limit(20),
    ])
      .then(([redemptionResult, paymentResult]) => {
        if (redemptionResult.error) throw redemptionResult.error;
        if (paymentResult.error) throw paymentResult.error;
        setRedemptions((redemptionResult.data || []) as MemberRedemption[]);
        setPayments((paymentResult.data || []) as MemberPayment[]);
      })
      .catch((error: unknown) => {
        setActivityError(
          error instanceof Error
            ? error.message
            : "Could not load your membership activity.",
        );
      })
      .finally(() => setActivityLoading(false));
  }, [membership, session]);
  if (!session)
    return (
      <div className="dashboard-shell">
        <SignInPanel title="Log in to your Coffee Pass" allowSignup={false} />
      </div>
    );
  if (loading)
    return (
      <div className="dashboard-shell">
        <div className="loading-block">Opening your coffee pass…</div>
      </div>
    );
  if (!membership)
    return (
      <div className="dashboard-shell">
        <div className="empty-membership">
          <Coffee />
          <p className="eyebrow">No membership yet</p>
          <h2>Your coffee pass is ready when you are.</h2>
          <p>
            Choose a published plan or ask the Solo team to onboard you at the
            counter using your verified mobile number.
          </p>
          <NavLink className="button button--red" to="/membership">
            Explore Coffee Club
          </NavLink>
        </div>
      </div>
    );
  if (membership.status === "pending" && !membership.starts_at)
    return (
      <div className="dashboard-shell">
        <div className="empty-membership">
          <Clock3 />
          <p className="eyebrow">Payment pending</p>
          <h2>Your plan request is saved.</h2>
          <p>
            The Solo team can now find your mobile number in the admin panel.
            Pay at the counter and ask staff to verify the payment; your 30-day
            pass starts only after activation.
          </p>
          <div className="button-row">
            <NavLink className="button button--red" to="/membership">
              View plan details
            </NavLink>
            <button
              className="button button--outline"
              onClick={() => supabase?.auth.signOut()}
            >
              Switch account
            </button>
          </div>
        </div>
      </div>
    );
  const balance = embeddedOne(membership.membership_balances) || {
    purchased: 0,
    used: 0,
    remaining: 0,
  };
  const surprise = embeddedOne(membership.membership_surprises);
  const surpriseCountdown = surprise
    ? Math.max(surprise.unlock_redemption_number - balance.used, 0)
    : null;
  const activity = [
    ...redemptions.map((item) => ({
      id: item.id,
      label: item.drink_name,
      when: item.created_at,
      detail: item.upgrade_amount
        ? `₹${item.upgrade_amount} upgrade${item.reversed_at ? " · Reversed" : ""}`
        : item.reversed_at
          ? "Reversed redemption"
          : "Coffee redemption",
    })),
    ...payments.map((item) => ({
      id: item.id,
      label: `${item.kind} payment`,
      when: item.created_at,
      detail: `₹${item.amount} · ${item.status}`,
    })),
  ].sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  const recentActivityPanel = (
    <article className="history-panel">
      <p className="eyebrow">Recent activity</p>
      <h3>Redemptions & payments</h3>
      {activityLoading ? (
        <p className="form-note">Loading your latest activity…</p>
      ) : activityError ? (
        <p className="error-text">{activityError}</p>
      ) : activity.length ? (
        <div className="history-list">
          {activity.map((item) => (
            <div className="history-row" key={item.id}>
              <strong>{item.label}</strong>
              <span>{new Date(item.when).toLocaleString("en-IN")}</span>
              <span>{item.detail}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="form-note">No transactions on this membership yet.</p>
      )}
    </article>
  );
  const dashboardSections: Array<{
    id: MemberSection;
    label: string;
    icon: typeof LayoutDashboard;
  }> = [
    { id: "pass", label: "My pass", icon: LayoutDashboard },
    { id: "history", label: "History", icon: History },
    { id: "payments", label: "Payments", icon: WalletCards },
  ];
  const sectionHeading =
    memberSection === "history"
      ? "Redemption history"
      : memberSection === "payments"
        ? "Payments"
        : "My Coffee Pass";
  return (
    <div className="dashboard-page">
      <aside className="dashboard-nav">
        <Logo light />
        <p>Member space</p>
        {dashboardSections.map(({ id, label, icon: Icon }) => (
          <button
            className={`nav-button ${memberSection === id ? "active" : ""}`}
            onClick={() => setMemberSection(id)}
            aria-pressed={memberSection === id}
            key={id}
          >
            <Icon /> {label}
          </button>
        ))}
        <button onClick={() => supabase?.auth.signOut()}>Sign out</button>
      </aside>
      <section className="dashboard-main">
        <div className="dashboard-title">
          <div>
            <p className="eyebrow">Good to see you</p>
            <h1>{sectionHeading}</h1>
          </div>
          <span className={`status status--${membership.status}`}>
            {membership.status}
          </span>
        </div>
        <nav
          className="member-section-tabs"
          aria-label="Member dashboard sections"
        >
          {dashboardSections.map(({ id, label, icon: Icon }) => (
            <button
              className={memberSection === id ? "active" : ""}
              onClick={() => setMemberSection(id)}
              aria-pressed={memberSection === id}
              key={id}
            >
              <Icon /> {label}
            </button>
          ))}
        </nav>
        {memberSection === "pass" && (
          <div className="dashboard-grid">
            <article className="balance-card">
              {membership.subscription_plans.is_unlimited ? (
                <>
                  <p>YOUR COFFEE PASS</p>
                  <strong className="balance-card__unlimited">Unlimited</strong>
                  <p>
                    {balance.used} coffees enjoyed · Redeem again after{" "}
                    {formatRedemptionGap(
                      membership.subscription_plans
                        .minimum_redemption_gap_minutes,
                    )}
                  </p>
                </>
              ) : (
                <>
                  <p>COFFEES REMAINING</p>
                  <strong>{balance.remaining}</strong>
                  <div className="progress">
                    <span
                      style={{
                        width: `${balance.purchased ? (balance.remaining / balance.purchased) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div>
                    <span>{balance.used} used</span>
                    <span>{balance.purchased} purchased</span>
                  </div>
                </>
              )}
            </article>
            <article className="qr-card">
              <p className="eyebrow">Refreshes automatically</p>
              {qrToken ? (
                <QRCodeSVG value={qrToken} size={180} marginSize={2} />
              ) : (
                <div className="qr-placeholder">
                  <ShieldCheck />
                  <span>{qrError || "Creating your secure QR…"}</span>
                </div>
              )}
              <strong>Show this at the counter</strong>
              <small>
                Only the latest QR works. It refreshes every 45 seconds.
              </small>
            </article>
            <article className="plan-summary">
              <p className="eyebrow">Active plan</p>
              <h3>{membership.subscription_plans.name}</h3>
              <dl>
                <div>
                  <dt>Starts</dt>
                  <dd>
                    {membership.starts_at
                      ? new Date(membership.starts_at).toLocaleDateString(
                          "en-IN",
                        )
                      : "At activation"}
                  </dd>
                </div>
                <div>
                  <dt>Expires</dt>
                  <dd>
                    {membership.expires_at
                      ? new Date(membership.expires_at).toLocaleDateString(
                          "en-IN",
                        )
                      : "30 days after activation"}
                  </dd>
                </div>
                <div>
                  <dt>Upgrade</dt>
                  <dd>₹49 when eligible</dd>
                </div>
              </dl>
              <a
                href={whatsappUrl(
                  "Hi Solo Café, I need help with my Coffee Club membership.",
                )}
                target="_blank"
                rel="noreferrer"
                className="text-link"
              >
                Get membership help <ArrowRight size={16} />
              </a>
            </article>
            {queuedMembership && (
              <article className="queued-pass-card">
                <Clock3 />
                <p className="eyebrow">Next pass queued</p>
                <h3>{queuedMembership.subscription_plans.name}</h3>
                <p>
                  Paid and ready. It activates automatically when your current
                  pass ends on{" "}
                  {queuedMembership.starts_at
                    ? new Date(queuedMembership.starts_at).toLocaleDateString(
                        "en-IN",
                      )
                    : "the scheduled date"}
                  .
                </p>
              </article>
            )}
            {surprise && (
              <article className="surprise-card">
                <Sparkles />
                <p className="eyebrow">A Solo surprise is coming</p>
                {surprise.status === "scheduled" ? (
                  <>
                    <strong>{surpriseCountdown}</strong>
                    <h3>
                      {surpriseCountdown === 1
                        ? "coffee until your surprise"
                        : "coffees until your surprise"}
                    </h3>
                    <p>The treat stays secret until the special redemption.</p>
                  </>
                ) : (
                  <>
                    <strong>🎉</strong>
                    <h3>Your surprise was {surprise.appetizer_name}</h3>
                    <p>This one-time treat has been claimed.</p>
                  </>
                )}
              </article>
            )}
            {recentActivityPanel}
          </div>
        )}
        {memberSection === "history" && (
          <section
            className="member-section-panel"
            aria-labelledby="member-history-title"
          >
            <p className="eyebrow">Coffee activity</p>
            <h2 id="member-history-title">Your redemption history</h2>
            {activityLoading ? (
              <p className="form-note">Loading your redemptions…</p>
            ) : activityError ? (
              <p className="error-text">{activityError}</p>
            ) : redemptions.length ? (
              <div className="history-list">
                {redemptions.map((item) => (
                  <div className="history-row" key={item.id}>
                    <strong>{item.drink_name}</strong>
                    <span>{formatIndiaDateTime(item.created_at)}</span>
                    <span>
                      {item.upgrade_amount
                        ? `₹${item.upgrade_amount} upgrade`
                        : item.source === "backdated"
                          ? "Backdated coffee"
                          : "Coffee redemption"}
                      {item.source === "backdated" && item.upgrade_amount
                        ? " · Backdated"
                        : ""}
                      {item.reversed_at && " · Reversed"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="form-note">No coffee redemptions yet.</p>
            )}
          </section>
        )}
        {memberSection === "payments" && (
          <section
            className="member-section-panel"
            aria-labelledby="member-payments-title"
          >
            <p className="eyebrow">Payment records</p>
            <h2 id="member-payments-title">Your Coffee Club payments</h2>
            {activityLoading ? (
              <p className="form-note">Loading your payments…</p>
            ) : activityError ? (
              <p className="error-text">{activityError}</p>
            ) : payments.length ? (
              <div className="member-payment-list">
                {payments.map((payment) => (
                  <article className="member-payment-row" key={payment.id}>
                    <div>
                      <strong>{payment.kind} payment</strong>
                      <span>
                        {new Date(payment.created_at).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <strong>₹{payment.amount}</strong>
                    <span className={`status status--${payment.status}`}>
                      {payment.status}
                    </span>
                  </article>
                ))}
              </div>
            ) : (
              <p className="form-note">
                No payments recorded for this membership yet.
              </p>
            )}
          </section>
        )}
      </section>
    </div>
  );
}

function JoinPage({ session }: { session: Session | null }) {
  type JoinPlanOption = {
    id: string;
    name: string;
    price: number;
    coffee_count: number;
    validity_days: number;
    is_unlimited: boolean;
    minimum_redemption_gap_minutes: number;
  };
  const [name, setName] = useState("");
  const [office, setOffice] = useState("");
  const [officeSuggestions, setOfficeSuggestions] = useState<string[]>([]);
  const [consent, setConsent] = useState(true);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [planOptions, setPlanOptions] = useState<JoinPlanOption[]>([]);
  const [plansUserId, setPlansUserId] = useState("");
  const [planError, setPlanError] = useState("");
  const params = new URLSearchParams(useLocation().search);
  const planId = params.get("plan");
  const navigate = useNavigate();
  const phoneValue = session?.user.phone?.replace(/\D/g, "").slice(-10) ?? "";
  useEffect(() => {
    if (!supabase || !session) return;
    void supabase
      .from("subscription_plans")
      .select(
        "id,name,price,coffee_count,validity_days,is_unlimited,minimum_redemption_gap_minutes",
      )
      .eq("active", true)
      .order("sort_order")
      .then(({ data, error }) => {
        if (error) {
          setPlanError("The Coffee Club plans could not be loaded. Try again.");
          setPlanOptions([]);
        } else {
          setPlanError("");
          setPlanOptions((data || []) as JoinPlanOption[]);
        }
        setPlansUserId(session.user.id);
      });
  }, [session]);
  useEffect(() => {
    if (!supabase || !session) return;
    void Promise.all([
      supabase
        .from("profiles")
        .select("full_name,offices(name)")
        .eq("id", session.user.id)
        .maybeSingle(),
      supabase.from("offices").select("name").order("name").limit(200),
    ]).then(([profileResult, officeResult]) => {
      if (profileResult.data?.full_name) setName(profileResult.data.full_name);
      const profileOffice = embeddedOne(profileResult.data?.offices)?.name;
      if (profileOffice) setOffice(profileOffice);
      setOfficeSuggestions((officeResult.data || []).map((item) => item.name));
    });
  }, [session]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!session || !supabase) return;
    const customerName = name.trim();
    const normalized = phoneValue.replace(/\D/g, "");
    const officeName = office.trim();
    if (customerName.length < 2) return setMessage("Enter the customer name.");
    if (normalized.length !== 10)
      return setMessage("Enter a valid 10-digit Indian mobile number.");
    if (!planId)
      return setMessage("Choose a Coffee Club plan before continuing.");
    if (officeName && officeName.length < 2)
      return setMessage("Enter at least 2 characters for the office name.");
    try {
      setSaving(true);
      setMessage("");
      let officeId: string | null = null;
      if (officeName) {
        const { data, error } = await supabase.rpc("get_or_create_office", {
          p_name: officeName,
        });
        if (error) throw error;
        officeId = data;
      }
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: customerName,
          whatsapp_consent: consent,
          office_id: officeId,
        })
        .eq("id", session.user.id)
        .select("id")
        .single();
      if (profileError) throw profileError;
      const { error: requestError } = await supabase.rpc("request_membership", {
        p_plan_id: planId,
      });
      if (requestError) throw requestError;
      navigate("/dashboard", { replace: true });
    } catch (error) {
      const raw =
        typeof error === "object" && error && "message" in error
          ? String(error.message)
          : "";
      const known: Record<string, string> = {
        membership_already_pending_payment:
          "You’re already signed up with us. Your next step is to complete payment at the Solo counter.",
        membership_already_active:
          "You already have an active Coffee Pass. Ask Solo staff to queue your next plan; it will start after your current pass ends.",
        membership_renewal_already_queued:
          "Your next Coffee Pass is already paid and queued to start after your current pass ends.",
        member_phone_verification_required:
          "Verify your Indian mobile number by OTP before joining the Coffee Club.",
      };
      setMessage(
        known[raw] ||
          raw ||
          "We could not save your plan request. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }
  const selectedPlan = planOptions.find((plan) => plan.id === planId);
  const joinStep = resolveJoinStep(
    Boolean(session),
    Boolean(session && plansUserId !== session.user.id),
    planId,
    Boolean(selectedPlan),
  );
  if (joinStep === "sign-in")
    return (
      <div className="dashboard-shell">
        <SignInPanel title="Sign in to join the Coffee Club" />
      </div>
    );
  if (joinStep === "loading")
    return (
      <div className="dashboard-shell">
        <div className="loading-block">Preparing your Coffee Club plans…</div>
      </div>
    );
  if (joinStep === "choose-plan")
    return (
      <div className="join-page join-page--choices">
        <section className="join-plan-select" aria-labelledby="join-plan-title">
          <p className="eyebrow">Mobile number verified</p>
          <h1 id="join-plan-title">Choose your Coffee Club plan.</h1>
          <p>
            Pick the plan that fits your month. Payment is completed at the Solo
            counter after you save your details.
          </p>
          {planError ? (
            <p className="error-text">{planError}</p>
          ) : (
            <div className="plan-grid join-plan-grid">
              {planOptions.map((plan) => (
                <article
                  className={`plan-card ${
                    plan.name === "Solo Club 20" ? "plan-card--best" : ""
                  } ${plan.is_unlimited ? "plan-card--premium" : ""}`}
                  key={plan.id}
                >
                  <div className="plan-card__topline">
                    <p className="eyebrow">{plan.validity_days} days</p>
                    {plan.name === "Solo Club 20" && (
                      <span className="plan-tier-badge">Best Value</span>
                    )}
                    {plan.is_unlimited && (
                      <span className="plan-tier-badge">Premium</span>
                    )}
                  </div>
                  <h3>{plan.name}</h3>
                  <Price value={plan.price} />
                  <strong className="plan-card__headline">
                    {plan.is_unlimited
                      ? "Unlimited included coffees"
                      : `${plan.coffee_count} coffees`}
                  </strong>
                  <p className="plan-card__promise">
                    {plan.is_unlimited
                      ? `Redeem again after ${formatRedemptionGap(plan.minimum_redemption_gap_minutes)}.`
                      : "Maximum 3 coffees per day."}
                    {plan.name === "Solo Club 20" && (
                      <strong>Works out to under ₹75 per coffee.</strong>
                    )}
                  </p>
                  <div className="plan-card__actions">
                    <NavLink
                      to={`/join?plan=${plan.id}`}
                      className={`button ${
                        plan.is_unlimited ? "button--cream" : "button--red"
                      }`}
                    >
                      Choose {plan.name} <ArrowRight size={17} />
                    </NavLink>
                  </div>
                </article>
              ))}
            </div>
          )}
          <button
            type="button"
            className="text-link join-plan-signout"
            onClick={() => supabase?.auth.signOut({ scope: "local" })}
          >
            Not your mobile number? Sign out
          </button>
        </section>
      </div>
    );
  return (
    <div className="join-page">
      <form className="join-form" onSubmit={submit}>
        <p className="eyebrow">Membership setup</p>
        <h1>Connect your coffee routine.</h1>
        <p>
          Your selected plan: <strong>{selectedPlan?.name}</strong>. Payment is
          completed offline with Solo staff.{" "}
          <NavLink to="/join" className="text-link">
            Change plan
          </NavLink>
        </p>
        <label>
          Full name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            minLength={2}
            maxLength={80}
            autoComplete="name"
            placeholder="Customer name"
            required
          />
        </label>
        <label>
          Indian mobile number
          <div className="phone-input">
            <span>+91</span>
            <input
              inputMode="numeric"
              value={phoneValue}
              readOnly
              aria-readonly="true"
              maxLength={10}
              placeholder="98765 43210"
              required
            />
          </div>
          <span className="form-note">
            Verified by OTP. Sign out to use a different number.
          </span>
        </label>
        <label>
          <span className="field-label">
            Your office <span className="optional-label">Optional</span>
          </span>
          <input
            list="join-office-suggestions"
            value={office}
            onChange={(event) => setOffice(event.target.value)}
            minLength={2}
            maxLength={120}
            autoComplete="organization"
            placeholder="Company or office name"
          />
          <datalist id="join-office-suggestions">
            {officeSuggestions.map((suggestion) => (
              <option key={suggestion} value={suggestion} />
            ))}
          </datalist>
          <span className="office-incentive">
            <strong>Help unlock an office coffee deal.</strong> Tell us where
            you work. If enough colleagues join, Solo can prepare a company
            Coffee Club pack for your HR team. You can skip this.
          </span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
            required
          />
          <span>
            I consent to essential membership and redemption updates on
            WhatsApp.
          </span>
        </label>
        <label className="check-row">
          <input type="checkbox" defaultChecked required />
          <span>I accept the Coffee Club subscription conditions.</span>
        </label>
        <button className="button button--red" type="submit" disabled={saving}>
          {saving ? (
            "Saving…"
          ) : (
            <>
              Save and continue <ArrowRight size={18} />
            </>
          )}
        </button>
        {message && <p className="form-note">{message}</p>}
        <p className="fine-print">
          Opening an enquiry or sharing a payment screenshot does not activate a
          plan. A staff member records verified payment at the counter.
        </p>
      </form>
    </div>
  );
}

function StaffPage({ session }: { session: Session | null }) {
  type DrinkRule = {
    drink_code: string;
    drink_name: string;
    upgrade_allowed: boolean;
    upgrade_price: number | null;
  };
  type ScanResult = {
    ok?: boolean;
    message?: string;
    manual?: boolean;
    member?: {
      membershipId: string;
      name: string;
      maskedPhone: string;
      plan: string;
      remaining: number | null;
      unlimited: boolean;
      dailyLimit: number;
      redeemedToday: number;
      dailyLimitReached: boolean;
      minimumGapMinutes: number;
      expiresAt: string;
      drinks: DrinkRule[];
      history: Array<{
        id: string;
        drink_name: string;
        upgrade_amount: number;
        created_at: string;
        reversed_at: string | null;
        reversal_reason: string | null;
        source: string;
      }>;
      surprise: {
        appetizerName: string;
        unlockRedemptionNumber: number;
        redemptionsUntil: number;
        dueNow: boolean;
      } | null;
    };
    redemption?: {
      redemption_id: string;
      remaining: number | null;
      unlimited?: boolean;
      upgrade_amount: number;
      surprise?: { appetizer_name: string } | null;
    };
    reversal?: {
      redemption_id: string;
      remaining: number | null;
      unlimited?: boolean;
    };
  };
  const [token, setToken] = useState("");
  const [result, setResult] = useState<ScanResult | null>(null);
  const [selectedDrink, setSelectedDrink] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingMember, setCheckingMember] = useState(false);
  const validationInFlightRef = useRef(false);
  const scanHandledRef = useRef(false);
  const scannerRef = useRef<Html5QrcodeInstance | null>(null);
  const scannerLibraryRef = useRef<typeof import("html5-qrcode") | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  useEffect(() => {
    if (!supabase || !session) return;
    void supabase
      .from("staff_roles")
      .select("profile_id")
      .eq("profile_id", session.user.id)
      .eq("active", true)
      .maybeSingle()
      .then(({ data }) => setAuthorized(Boolean(data)));
  }, [session]);
  useEffect(() => {
    let cancelled = false;
    void import("html5-qrcode")
      .then((library) => {
        if (cancelled) return;
        scannerLibraryRef.current = library;
        setCameraReady(true);
      })
      .catch(() => {
        if (!cancelled)
          setCameraError(
            "The camera scanner could not load. Refresh this page and try again.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, []);
  async function request(payload: Record<string, unknown>) {
    setResult(null);
    const response = await fetch(edgeFunctionUrl("redeem"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token || ""}`,
      },
      body: JSON.stringify(payload),
    });
    const data = (await response.json()) as ScanResult;
    setResult(data);
    return data;
  }
  async function validate(value = token) {
    if (validationInFlightRef.current) return;
    validationInFlightRef.current = true;
    setCheckingMember(true);
    try {
      const digits = value.replace(/\D/g, "");
      const data =
        digits.length === 10
          ? await request({ action: "lookup", phone: `+91${digits}` })
          : await request({ action: "validate", token: value });
      setSelectedDrink("");
      setManualReason(data.manual ? "Phone lookup at counter" : "");
    } catch {
      setResult({
        ok: false,
        message:
          "The member check did not complete. Please wait a moment and try again.",
      });
    } finally {
      validationInFlightRef.current = false;
      setCheckingMember(false);
    }
  }
  async function confirmRedemption() {
    if (!result?.member || !selectedDrink) return;
    const drink = result.member.drinks.find(
      (item) => item.drink_code === selectedDrink,
    );
    if (!drink) return;
    setSubmitting(true);
    try {
      const payload = result.manual
        ? {
            action: "confirmManual",
            membershipId: result.member.membershipId,
            drinkCode: drink.drink_code,
            drinkName: drink.drink_name,
            upgrade: drink.upgrade_allowed,
            reason: manualReason || "Phone lookup at counter",
            idempotencyKey: crypto.randomUUID(),
          }
        : {
            action: "confirm",
            token,
            drinkCode: drink.drink_code,
            drinkName: drink.drink_name,
            upgrade: drink.upgrade_allowed,
            idempotencyKey: crypto.randomUUID(),
          };
      await request(payload);
    } finally {
      setSubmitting(false);
    }
  }
  async function reverseRedemption(redemptionId: string) {
    const reason = window.prompt(
      "Explain why this redemption is being reversed.",
    );
    if (reason === null) return;
    if (reason.trim().length < 5) {
      setResult({
        ok: false,
        message: "Enter an explanation of at least 5 characters.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await request({
        action: "reverse",
        redemptionId,
        reason: reason.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  }
  async function stopCamera() {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    try {
      if (scanner?.isScanning) await scanner.stop();
    } catch {
      // The camera track may already be closed by the browser.
    }
    try {
      scanner?.clear();
    } catch {
      // Clearing an already-disposed scanner is harmless.
    }
    setCameraOn(false);
  }
  async function startCamera() {
    setCameraError("");
    setCameraStarting(true);
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera access is not supported in this browser.");
      }
      const scannerLibrary = scannerLibraryRef.current;
      if (!scannerLibrary) throw new Error("Camera scanner is still loading.");
      const { Html5Qrcode } = scannerLibrary;
      await stopCamera();
      const scannerConfig = {
        fps: 10,
        aspectRatio: 1,
        qrbox: (width: number, height: number) => {
          const size = Math.min(
            260,
            Math.max(120, Math.min(width, height) - 32),
          );
          return { width: size, height: size };
        },
      };
      const onDecoded = (decoded: string) => {
        if (scanHandledRef.current) return;
        scanHandledRef.current = true;
        setToken(decoded);
        void stopCamera().finally(() => void validate(decoded));
      };
      scanHandledRef.current = false;
      let scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      try {
        // Asking for the environment-facing camera directly is the most
        // reliable path on iOS Safari and Android Chrome.
        await scanner.start(
          { facingMode: "environment" },
          scannerConfig,
          onDecoded,
          () => undefined,
        );
      } catch (firstError) {
        const detail =
          firstError instanceof Error
            ? `${firstError.name} ${firstError.message}`
            : String(firstError);
        if (/notallowed|permission|denied/i.test(detail)) throw firstError;
        try {
          scanner.clear();
        } catch {
          // Rebuild the scanner before trying a concrete device id.
        }
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras.length) throw firstError;
        const preferredCamera =
          cameras.find((camera) =>
            /back|rear|environment/i.test(camera.label || ""),
          ) || cameras[0];
        scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          preferredCamera.id,
          scannerConfig,
          onDecoded,
          () => undefined,
        );
      }
      setCameraOn(true);
    } catch (error) {
      await stopCamera();
      const detail =
        error instanceof Error
          ? `${error.name} ${error.message}`
          : String(error);
      if (/notallowed|permission|denied/i.test(detail)) {
        setCameraError(
          "Camera access is blocked. Open your browser’s settings for solocafe.in, allow Camera, then reload this page.",
        );
      } else if (/notfound|no camera|devicesnotfound/i.test(detail)) {
        setCameraError(
          "No camera was found. Connect or enable a camera, then try again.",
        );
      } else if (
        /notreadable|trackstart|in use|could not start/i.test(detail)
      ) {
        setCameraError(
          "The camera is being used by another app. Close other camera or video-call apps, then try again.",
        );
      } else {
        setCameraError(
          "The camera could not start. Check the browser’s camera permission for solocafe.in and try again.",
        );
      }
    } finally {
      setCameraStarting(false);
    }
  }
  useEffect(
    () => () => {
      void stopCamera();
    },
    [],
  );
  function memberActivity(member: NonNullable<ScanResult["member"]>) {
    return (
      <>
        {!member.unlimited && member.dailyLimitReached && (
          <div className="daily-limit-alert" role="status" aria-live="polite">
            <Coffee aria-hidden="true" />
            <div>
              <strong>Daily coffee limit reached</strong>
              <span>
                {member.redeemedToday} of {member.dailyLimit} coffees used
                today. The next redemption is available tomorrow.
              </span>
            </div>
          </div>
        )}
        <section
          className="staff-coffee-history"
          aria-labelledby="coffee-history-title"
        >
          <div className="staff-coffee-history__heading">
            <History aria-hidden="true" />
            <h3 id="coffee-history-title">Recent coffee history</h3>
          </div>
          {member.history.length ? (
            <ol>
              {member.history.map((entry) => (
                <li
                  key={entry.id}
                  className={entry.reversed_at ? "is-reversed" : ""}
                >
                  <div>
                    <strong>{entry.drink_name}</strong>
                    <span>{formatIndiaDateTime(entry.created_at)}</span>
                  </div>
                  <div className="coffee-history-meta">
                    {entry.upgrade_amount > 0 && (
                      <span>₹{entry.upgrade_amount} upgrade</span>
                    )}
                    {entry.source === "backdated" && <span>Backdated</span>}
                    {entry.reversed_at && <strong>Reversed</strong>}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="coffee-history-empty">
              No coffees redeemed on this pass yet.
            </p>
          )}
        </section>
      </>
    );
  }
  if (!session)
    return (
      <div className="dashboard-shell">
        <SignInPanel
          title="Staff access"
          method="email"
          redirectPath="/staff"
          allowPassword
        />
      </div>
    );
  if (authorized === null)
    return <div className="loading-block">Checking staff access…</div>;
  if (!authorized)
    return (
      <div className="dashboard-shell">
        <div className="empty-membership">
          <ShieldCheck />
          <h2>Approved staff only.</h2>
          <p>
            Signing in does not create staff access. Ask the Solo owner to add
            this email, or sign out and use an approved staff account.
          </p>
          <button
            className="button button--red"
            onClick={() => supabase?.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  return (
    <div className="staff-page">
      <div className="staff-header">
        <div>
          <p className="eyebrow">Solo Café operations</p>
          <h1>Scan a Coffee Pass</h1>
        </div>
        <NavLink to="/admin" className="button button--outline">
          <LayoutDashboard size={17} /> Admin
        </NavLink>
      </div>
      <div className="scanner-layout">
        <section className="scanner-card">
          <div className="qr-reader-shell">
            <div
              id="qr-reader"
              className={cameraOn ? "qr-reader active" : "qr-reader"}
              role="region"
              aria-label="Coffee Pass QR camera scanner"
            />
            {!cameraOn && (
              <div className="qr-reader-placeholder" aria-hidden="true">
                <Camera />
              </div>
            )}
          </div>
          <button
            className="button button--red full"
            type="button"
            onClick={cameraOn ? () => void stopCamera() : startCamera}
            disabled={cameraStarting || !cameraReady}
            aria-pressed={cameraOn}
          >
            <Camera size={18} />{" "}
            {!cameraReady
              ? "Preparing camera…"
              : cameraStarting
                ? "Opening camera…"
                : cameraOn
                  ? "Stop camera"
                  : "Start camera"}
          </button>
          {cameraError && (
            <p className="camera-error" role="alert" aria-live="polite">
              {cameraError}
            </p>
          )}
          <div className="or">
            <span>or use QR token / 10-digit mobile</span>
          </div>
          <label className="search-box">
            <Search size={20} />
            <input
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="QR token or mobile number"
            />
          </label>
          <button
            className="button button--ink full"
            onClick={() => validate()}
            disabled={!token || checkingMember}
          >
            {checkingMember ? "Checking member…" : "Check member"}
          </button>
        </section>
        <section className="scan-result">
          {result?.reversal ? (
            <div className="result-placeholder">
              <ShieldCheck />
              <h3>Redemption reversed</h3>
              <p>
                The incorrect redemption was marked as reversed and the member’s
                plan was restored.
              </p>
              <button
                className="button button--red"
                onClick={() => {
                  setResult(null);
                  setToken("");
                  setSelectedDrink("");
                }}
              >
                Scan next member
              </button>
            </div>
          ) : result?.redemption ? (
            <div className="redemption-complete">
              <BadgeCheck />
              <h3>Redemption confirmed</h3>
              <p>
                {result.redemption.unlimited
                  ? "Unlimited plan recorded."
                  : `${result.redemption.remaining} coffees remain.`}{" "}
                {result.redemption.upgrade_amount
                  ? `₹${result.redemption.upgrade_amount} upgrade recorded.`
                  : "No upgrade charge."}
              </p>
              {result.redemption.surprise && (
                <div className="surprise-unlocked">
                  <Sparkles />
                  <strong>Solo surprise unlocked!</strong>
                  <span>
                    Give this member a free{" "}
                    {result.redemption.surprise.appetizer_name} now.
                  </span>
                </div>
              )}
              {result.member && memberActivity(result.member)}
              <div className="button-row">
                <button
                  className="button button--red"
                  onClick={() => {
                    setResult(null);
                    setToken("");
                    setSelectedDrink("");
                  }}
                >
                  Scan next member
                </button>
                <button
                  className="button button--outline"
                  onClick={() =>
                    void reverseRedemption(result.redemption!.redemption_id)
                  }
                  disabled={submitting}
                >
                  Reverse incorrect redemption
                </button>
              </div>
            </div>
          ) : result?.member ? (
            <>
              <span className="status status--active">Valid member</span>
              <h2>{result.member.name}</h2>
              <p>{result.member.maskedPhone}</p>
              <dl>
                <div>
                  <dt>Plan</dt>
                  <dd>{result.member.plan}</dd>
                </div>
                <div>
                  <dt>Remaining</dt>
                  <dd>
                    {result.member.unlimited
                      ? "Unlimited"
                      : `${result.member.remaining} coffees`}
                  </dd>
                </div>
                {!result.member.unlimited && (
                  <div>
                    <dt>Used today</dt>
                    <dd>
                      {result.member.redeemedToday} of{" "}
                      {result.member.dailyLimit}
                    </dd>
                  </div>
                )}
                {result.member.unlimited && (
                  <div>
                    <dt>Next redemption</dt>
                    <dd>
                      {result.member.minimumGapMinutes} minutes after the last
                      coffee
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Expires</dt>
                  <dd>{result.member.expiresAt}</dd>
                </div>
              </dl>
              {memberActivity(result.member)}
              {result.member.surprise?.dueNow && (
                <div className="surprise-due">
                  <Sparkles />
                  <div>
                    <strong>Surprise due on this redemption</strong>
                    <span>
                      Free {result.member.surprise.appetizerName} · one time
                      only
                    </span>
                  </div>
                </div>
              )}
              <label>
                Drink
                <select
                  value={selectedDrink}
                  onChange={(event) => setSelectedDrink(event.target.value)}
                >
                  <option value="">Select eligible drink</option>
                  {result.member.drinks.map((drink) => (
                    <option key={drink.drink_code} value={drink.drink_code}>
                      {drink.drink_name}
                      {drink.upgrade_allowed
                        ? ` · ₹${drink.upgrade_price ?? 49} upgrade`
                        : ""}
                    </option>
                  ))}
                </select>
              </label>
              {selectedDrink &&
                result.member.drinks.find(
                  (drink) => drink.drink_code === selectedDrink,
                )?.upgrade_allowed && (
                  <div className="form-note">
                    ₹
                    {result.member.drinks.find(
                      (drink) => drink.drink_code === selectedDrink,
                    )?.upgrade_price ?? 49}{" "}
                    Signature Coffee upgrade required and recorded with this
                    redemption.
                  </div>
                )}
              {result.manual && (
                <div className="form-note">
                  Phone fallback will be recorded automatically for the audit
                  log.
                </div>
              )}
              <button
                className="button button--red full"
                onClick={confirmRedemption}
                disabled={
                  !selectedDrink ||
                  submitting ||
                  result.member.dailyLimitReached
                }
              >
                {result.member.dailyLimitReached
                  ? "Daily limit reached"
                  : submitting
                    ? "Confirming…"
                    : "Confirm redemption"}
              </button>
            </>
          ) : (
            <div className="result-placeholder">
              <ShieldCheck />
              <h3>Member details appear here</h3>
              <p>
                Validation checks status, expiry, balance, daily limit and token
                freshness before any coffee is deducted.
              </p>
              {result?.message && (
                <p className="error-text">{result.message}</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function AccountPasswordUpdate({
  session,
  destination,
  destinationLabel,
}: {
  session: Session | null;
  destination: "/staff" | "/admin";
  destinationLabel: string;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [complete, setComplete] = useState(false);

  async function savePassword(event: FormEvent) {
    event.preventDefault();
    if (password.length < 10) {
      setMessage("Use at least 10 characters for the new password.");
      return;
    }
    if (password !== confirmation) {
      setMessage("The two passwords do not match.");
      return;
    }
    try {
      setSaving(true);
      setMessage("");
      const { error } = await updateAccountPassword(password);
      if (error) throw error;
      setComplete(true);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "The password could not be updated.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (!session)
    return (
      <div className="dashboard-shell">
        <div className="auth-panel">
          <div className="auth-panel__icon">
            <ShieldCheck />
          </div>
          <p className="eyebrow">Secure account password</p>
          <h2>Opening password setup…</h2>
          <p>
            If this message remains, the email link has expired. Return to the
            login and request a new password recovery email.
          </p>
          <NavLink className="button button--outline" to={destination}>
            Return to {destinationLabel} login
          </NavLink>
        </div>
      </div>
    );

  return (
    <div className="dashboard-shell">
      <div className="auth-panel">
        <div className="auth-panel__icon">
          <ShieldCheck />
        </div>
        <p className="eyebrow">Secure account password</p>
        <h2>{complete ? "Password ready" : "Create a new password"}</h2>
        {complete ? (
          <>
            <p>Your password has been updated securely.</p>
            <a className="button button--red" href={destination}>
              Continue to {destinationLabel}
            </a>
          </>
        ) : (
          <form className="magic-link-form" onSubmit={savePassword}>
            <label>
              New password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </label>
            <label>
              Confirm new password
              <input
                type="password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </label>
            <p className="form-note">
              Use at least 10 characters. A password manager is recommended.
            </p>
            <button
              className="button button--red"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save password"}
            </button>
          </form>
        )}
        {message && <p className="error-text">{message}</p>}
        <p className="fine-print">
          Password access does not grant staff permissions. Solo’s approved
          staff role is still required.
        </p>
      </div>
    </div>
  );
}

function StaffAccessPage({ session }: { session: Session | null }) {
  const { search, hash } = useLocation();
  const isPasswordReset =
    new URLSearchParams(search).get("reset-password") === "1" ||
    new URLSearchParams(hash.replace(/^#/, "")).get("type") === "recovery";
  return isPasswordReset ? (
    <AccountPasswordUpdate
      session={session}
      destination="/staff"
      destinationLabel="staff scanner"
    />
  ) : (
    <StaffPage session={session} />
  );
}

function AdminAccessPage({ session }: { session: Session | null }) {
  const { search, hash } = useLocation();
  const isPasswordReset =
    new URLSearchParams(search).get("reset-password") === "1" ||
    new URLSearchParams(hash.replace(/^#/, "")).get("type") === "recovery";
  return isPasswordReset ? (
    <AccountPasswordUpdate
      session={session}
      destination="/admin"
      destinationLabel="admin dashboard"
    />
  ) : (
    <AdminPage session={session} />
  );
}

function AdminPage({ session }: { session: Session | null }) {
  type AdminMember = {
    id: string;
    profile_id: string;
    membership_code: string;
    status: string;
    created_at: string;
    starts_at: string | null;
    expires_at: string | null;
    membership_sequence: number;
    returning_member: boolean;
    profiles: {
      full_name: string | null;
      phone_e164: string | null;
      office_id: string | null;
      offices: { name: string } | Array<{ name: string }> | null;
    };
    subscription_plans: {
      id: string;
      name: string;
      price: number;
      is_unlimited: boolean;
    };
    membership_balances:
      | { purchased: number; used: number; remaining: number }
      | Array<{ purchased: number; used: number; remaining: number }>;
    membership_surprises:
      | {
          id: string;
          appetizer_code: string;
          appetizer_name: string;
          unlock_redemption_number: number;
          status: "scheduled" | "claimed";
          claimed_at: string | null;
        }
      | Array<{
          id: string;
          appetizer_code: string;
          appetizer_name: string;
          unlock_redemption_number: number;
          status: "scheduled" | "claimed";
          claimed_at: string | null;
        }>;
  };
  type AdminPayment = {
    id: string;
    amount: number;
    kind: string;
    method: string;
    status: string;
    provider_reference: string | null;
    paid_at: string | null;
    created_at: string;
    cancelled_reason: string | null;
    cancelled_at: string | null;
    profiles: { full_name: string | null; phone_e164: string | null };
    memberships: {
      membership_code: string;
      subscription_plans: { name: string };
    } | null;
  };
  type AdminStaff = {
    profile_id: string;
    role: "staff" | "admin";
    active: boolean;
    granted_at: string;
    profiles: {
      full_name: string | null;
      email: string | null;
      phone_e164: string | null;
    };
  };
  type AdminDrinkRule = {
    plan_id: string;
    drink_code: string;
    drink_name: string;
    upgrade_allowed: boolean;
    upgrade_price: number | null;
  };
  type WebsiteEngagement = {
    path: string;
    visits: number;
    total_active_seconds: number;
    average_active_seconds: number;
    last_activity: string;
  };
  type CorporateCompany = {
    id: number;
    name: string;
    office_location: string;
    employee_tier: number;
    monthly_bill: number;
    status: "pending" | "active" | "inactive";
    starts_on: string | null;
    expires_on: string | null;
  };
  type CorporateEmployee = {
    id: number;
    company_id: number;
    full_name: string;
    phone_e164: string;
    email: string | null;
    active: boolean;
    profile_id: string | null;
    membership_starts_on: string | null;
    membership_expires_on: string | null;
  };
  type CorporateEnquiry = {
    id: number;
    company_name: string;
    contact_person: string;
    designation: string;
    work_email: string;
    mobile_number: string;
    office_location: string;
    employees_covered: number;
    preferred_start_date: string | null;
    notes: string | null;
    status: "new" | "contacted" | "converted" | "closed";
    created_at: string;
  };
  type CorporateUsage = {
    company_id: number;
    active_employees: number;
    coffees_redeemed: number;
    signature_upgrade_value: number;
  };
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [staff, setStaff] = useState<AdminStaff[]>([]);
  const [websiteEngagement, setWebsiteEngagement] = useState<
    WebsiteEngagement[]
  >([]);
  const [corporateCompanies, setCorporateCompanies] = useState<
    CorporateCompany[]
  >([]);
  const [corporateEmployees, setCorporateEmployees] = useState<
    CorporateEmployee[]
  >([]);
  const [corporateEnquiries, setCorporateEnquiries] = useState<
    CorporateEnquiry[]
  >([]);
  const [corporateUsage, setCorporateUsage] = useState<CorporateUsage[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [mostOrderedCounts, setMostOrderedCounts] = useState<
    MostOrderedCount[]
  >([]);
  const [plans, setPlans] = useState<
    Array<{ id: string; name: string; price: number }>
  >([]);
  const [planDrinkRules, setPlanDrinkRules] = useState<AdminDrinkRule[]>([]);
  const [offices, setOffices] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [query, setQuery] = useState("");
  const [paymentQuery, setPaymentQuery] = useState("");
  const [adminSection, setAdminSection] = useState<
    "overview" | "companies" | "payments" | "ticker" | "insights" | "roles"
  >("overview");
  const [statusFilter, setStatusFilter] = useState<
    "pending" | "queued" | "all" | "active" | "paused" | "lapsed"
  >("pending");
  const [showAdd, setShowAdd] = useState(false);
  const [activationMember, setActivationMember] = useState<AdminMember | null>(
    null,
  );
  const [editingMember, setEditingMember] = useState<AdminMember | null>(null);
  const [surpriseMember, setSurpriseMember] = useState<AdminMember | null>(
    null,
  );
  const [backdateMember, setBackdateMember] = useState<AdminMember | null>(
    null,
  );
  const [backdateDrinkCode, setBackdateDrinkCode] = useState("");
  const [backdateOccurredAt, setBackdateOccurredAt] = useState("");
  const [backdateMaximum, setBackdateMaximum] = useState("");
  const [backdateUpgrade, setBackdateUpgrade] = useState(false);
  const [onboardingSurprise, setOnboardingSurprise] = useState(false);
  const [notice, setNotice] = useState("");
  const [savingMember, setSavingMember] = useState(false);
  const [exportingClients, setExportingClients] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [metrics, setMetrics] = useState({
    active: 0,
    today: 0,
    pending: 0,
    queued: 0,
    expiring: 0,
    lapsed: 0,
  });
  const loadData = useCallback(async () => {
    if (!supabase || !session) return;
    setLoadingMembers(true);
    const { data: role, error: roleError } = await supabase
      .from("staff_roles")
      .select("role,active")
      .eq("profile_id", session.user.id)
      .eq("role", "admin")
      .eq("active", true)
      .maybeSingle();
    if (roleError) {
      setNotice(`Could not check admin access: ${roleError.message}`);
      setAuthorized(false);
      setLoadingMembers(false);
      return;
    }
    if (!role) {
      setAuthorized(false);
      setLoadingMembers(false);
      return;
    }
    setAuthorized(true);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const soon = new Date(Date.now() + 7 * 86400000).toISOString();
    const [
      memberRows,
      planRows,
      drinkRuleRows,
      officeRows,
      tickerRows,
      activeCount,
      todayCount,
      pendingCount,
      queuedCount,
      expiringCount,
      lapsedCount,
      paymentRows,
      staffRows,
      engagementRows,
      corporateCompanyRows,
      corporateEmployeeRows,
      corporateEnquiryRows,
      corporateUsageRows,
    ] = await Promise.all([
      supabase
        .from("memberships")
        .select(
          "id,profile_id,membership_code,status,starts_at,expires_at,created_at,membership_sequence,returning_member,profiles:profiles!memberships_profile_id_fkey(full_name,phone_e164,office_id,offices(name)),subscription_plans(id,name,price,is_unlimited),membership_balances(purchased,used,remaining),membership_surprises(id,appetizer_code,appetizer_name,unlock_redemption_number,status,claimed_at)",
        )
        .order("created_at", { ascending: false })
        .limit(1000),
      supabase
        .from("subscription_plans")
        .select("id,name,price")
        .eq("active", true)
        .order("sort_order"),
      supabase
        .from("plan_drink_rules")
        .select("plan_id,drink_code,drink_name,upgrade_allowed,upgrade_price")
        .eq("eligible", true)
        .order("drink_name"),
      supabase.from("offices").select("id,name").order("name").limit(500),
      supabase
        .from("most_ordered_counts")
        .select(
          "item_code,item_name,category,total_count,display_rank,active,updated_at",
        )
        .order("display_rank"),
      supabase
        .from("memberships")
        .select("id", { count: "exact", head: true })
        .eq("status", "active"),
      supabase
        .from("redemptions")
        .select("id", { count: "exact", head: true })
        .gte("created_at", today.toISOString()),
      supabase
        .from("memberships")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending")
        .is("starts_at", null),
      supabase
        .from("memberships")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending")
        .not("starts_at", "is", null),
      supabase
        .from("memberships")
        .select("id", { count: "exact", head: true })
        .eq("status", "active")
        .lte("expires_at", soon)
        .gte("expires_at", new Date().toISOString()),
      supabase
        .from("memberships")
        .select("id", { count: "exact", head: true })
        .in("status", ["expired", "cancelled"]),
      supabase
        .from("payments")
        .select(
          "id,amount,kind,method,status,provider_reference,paid_at,created_at,cancelled_reason,cancelled_at,profiles:profiles!payments_profile_id_fkey(full_name,phone_e164),memberships:memberships!payments_membership_id_fkey(membership_code,subscription_plans(name))",
        )
        .order("created_at", { ascending: false })
        .limit(300),
      supabase
        .from("staff_roles")
        .select(
          "profile_id,role,active,granted_at,profiles:profiles!staff_roles_profile_id_fkey(full_name,email,phone_e164)",
        )
        .order("granted_at", { ascending: false }),
      supabase.rpc("get_website_engagement", { p_days: 30 }),
      supabase
        .from("corporate_companies")
        .select(
          "id,name,office_location,employee_tier,monthly_bill,status,starts_on,expires_on",
        )
        .order("created_at", { ascending: false }),
      supabase
        .from("corporate_employees")
        .select(
          "id,company_id,full_name,phone_e164,email,active,profile_id,membership_starts_on,membership_expires_on",
        )
        .order("created_at", { ascending: false })
        .limit(5000),
      supabase
        .from("corporate_enquiries")
        .select(
          "id,company_name,contact_person,designation,work_email,mobile_number,office_location,employees_covered,preferred_start_date,notes,status,created_at",
        )
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("corporate_company_usage")
        .select(
          "company_id,active_employees,coffees_redeemed,signature_upgrade_value",
        ),
    ]);
    const loadError = [
      memberRows.error,
      planRows.error,
      drinkRuleRows.error,
      officeRows.error,
      tickerRows.error,
      activeCount.error,
      todayCount.error,
      pendingCount.error,
      queuedCount.error,
      expiringCount.error,
      lapsedCount.error,
      paymentRows.error,
      staffRows.error,
      engagementRows.error,
      corporateCompanyRows.error,
      corporateEmployeeRows.error,
      corporateEnquiryRows.error,
      corporateUsageRows.error,
    ].find(Boolean);
    if (loadError) {
      setNotice(`Could not load signups: ${loadError.message}`);
      setLoadingMembers(false);
      return;
    }
    setMembers((memberRows.data || []) as unknown as AdminMember[]);
    setPlans(planRows.data || []);
    setPlanDrinkRules(
      (drinkRuleRows.data || []) as unknown as AdminDrinkRule[],
    );
    setOffices(officeRows.data || []);
    setMostOrderedCounts(
      (tickerRows.data || []) as unknown as MostOrderedCount[],
    );
    setPayments((paymentRows.data || []) as unknown as AdminPayment[]);
    setStaff((staffRows.data || []) as unknown as AdminStaff[]);
    setWebsiteEngagement(
      (engagementRows.data || []) as unknown as WebsiteEngagement[],
    );
    setCorporateCompanies(
      (corporateCompanyRows.data || []) as CorporateCompany[],
    );
    setCorporateEmployees(
      (corporateEmployeeRows.data || []) as CorporateEmployee[],
    );
    setCorporateEnquiries(
      (corporateEnquiryRows.data || []) as CorporateEnquiry[],
    );
    setCorporateUsage((corporateUsageRows.data || []) as CorporateUsage[]);
    setMetrics({
      active: activeCount.count || 0,
      today: todayCount.count || 0,
      pending: pendingCount.count || 0,
      queued: queuedCount.count || 0,
      expiring: expiringCount.count || 0,
      lapsed: lapsedCount.count || 0,
    });
    setLoadingMembers(false);
  }, [session]);
  useEffect(() => {
    // Loading authenticated server state is the purpose of this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadData();
  }, [loadData]);
  const filtered = members.filter(
    (member) =>
      (statusFilter === "all" ||
        (statusFilter === "lapsed"
          ? member.status === "expired" || member.status === "cancelled"
          : statusFilter === "queued"
            ? member.status === "pending" && Boolean(member.starts_at)
            : statusFilter === "pending"
              ? member.status === "pending" && !member.starts_at
              : member.status === statusFilter)) &&
      `${member.profiles.full_name || ""} ${member.profiles.phone_e164 || ""} ${embeddedOne(member.profiles.offices)?.name || ""} ${member.membership_code}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const filteredPayments = payments.filter((payment) =>
    `${payment.profiles.full_name || ""} ${payment.profiles.phone_e164 || ""} ${payment.memberships?.membership_code || ""} ${payment.provider_reference || ""} ${payment.cancelled_reason || ""}`
      .toLowerCase()
      .includes(paymentQuery.toLowerCase()),
  );
  const isOwner = session?.user.email?.trim().toLowerCase() === OWNER_EMAIL;
  const selectedCompany =
    corporateCompanies.find((company) => company.id === selectedCompanyId) ||
    null;
  const selectedCompanyEmployees = corporateEmployees.filter(
    (employee) => employee.company_id === selectedCompanyId,
  );
  const companyUsage = new Map(
    corporateUsage.map((usage) => [usage.company_id, usage]),
  );
  const activationQueuesRenewal = Boolean(
    activationMember && ["active", "paused"].includes(activationMember.status),
  );
  async function api(payload: Record<string, unknown>) {
    setNotice("");
    try {
      const response = await fetch(edgeFunctionUrl("admin-members"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? ((await response.json()) as {
            ok?: boolean;
            message?: string;
            inviteSent?: boolean;
          })
        : {
            ok: false,
            message:
              "The local server function is not running. Restart the app with npm run dev.",
          };
      if (!response.ok || !data.ok)
        throw new Error(data.message || "Could not save the change.");
      setNotice(
        data.inviteSent
          ? "Role assigned and a secure password setup email was sent."
          : "Saved successfully.",
      );
      await loadData();
      return true;
    } catch (error) {
      setNotice(
        error instanceof Error ? error.message : "Could not save the change.",
      );
      return false;
    }
  }
  async function changeStatus(
    memberId: string,
    status: "active" | "paused" | "cancelled",
  ) {
    const reason =
      status === "active" ? "" : (window.prompt(`Reason for ${status}:`) ?? "");
    if (status !== "active" && reason.trim().length < 5) return;
    await api({ action: "status", membershipId: memberId, status, reason });
  }
  async function createCompany(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const startsOn = String(form.get("startsOn") || "") || null;
    const expiresOn = String(form.get("expiresOn") || "") || null;
    if (startsOn && expiresOn && expiresOn < startsOn) {
      setNotice("The company expiry date must be after its start date.");
      return;
    }
    setSavingMember(true);
    const ok = await api({
      action: "createCompany",
      name: form.get("companyName"),
      officeLocation: form.get("officeLocation"),
      employeeTier: Number(form.get("employeeTier")),
      monthlyBill: Number(form.get("monthlyBill")),
      startsOn,
      expiresOn,
    });
    setSavingMember(false);
    if (ok) formElement.reset();
  }
  async function importCorporateEmployees(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const companyId = Number(form.get("companyId"));
    const lines = String(form.get("employees") || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    const employees = lines.map((line) => {
      const [fullName = "", rawPhone = "", email = ""] = line
        .split(",")
        .map((part) => part.trim());
      const digits = rawPhone.replace(/\D/g, "").slice(-10);
      return { fullName, phone: `+91${digits}`, email: email || undefined };
    });
    if (
      !companyId ||
      !employees.length ||
      employees.some(
        (employee) =>
          !/^[+][9][1][6-9][0-9]{9}$/.test(employee.phone) ||
          employee.fullName.length < 2,
      )
    ) {
      setNotice(
        "Use one employee per line: Full name, 10-digit mobile, email (optional).",
      );
      return;
    }
    const company = corporateCompanies.find((item) => item.id === companyId);
    setSavingMember(true);
    const ok = await api({
      action: "importCorporateEmployees",
      companyId,
      startsOn: company?.starts_on || null,
      expiresOn: company?.expires_on || null,
      employees,
    });
    setSavingMember(false);
    if (ok) formElement.reset();
  }
  async function setCompanyStatus(
    company: CorporateCompany,
    status: CorporateCompany["status"],
  ) {
    await api({ action: "setCompanyStatus", companyId: company.id, status });
  }
  async function renewCompany(company: CorporateCompany) {
    const startsOn = window.prompt(
      "New cycle start date (YYYY-MM-DD):",
      new Date().toISOString().slice(0, 10),
    );
    if (!startsOn) return;
    const defaultExpiry = new Date(`${startsOn}T00:00:00`);
    defaultExpiry.setDate(defaultExpiry.getDate() + 29);
    const expiresOn = window.prompt(
      "New cycle expiry date (YYYY-MM-DD):",
      defaultExpiry.toISOString().slice(0, 10),
    );
    if (!expiresOn) return;
    await api({
      action: "renewCompany",
      companyId: company.id,
      startsOn,
      expiresOn,
    });
  }
  function prepareActivation(member: AdminMember) {
    setActivationMember(member);
    setEditingMember(null);
    setSurpriseMember(null);
    setBackdateMember(null);
    setOnboardingSurprise(false);
    setShowAdd(true);
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function prepareProfileEdit(member: AdminMember) {
    setEditingMember(member);
    setShowAdd(false);
    setActivationMember(null);
    setSurpriseMember(null);
    setBackdateMember(null);
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function prepareSurprise(member: AdminMember) {
    setSurpriseMember(member);
    setEditingMember(null);
    setShowAdd(false);
    setActivationMember(null);
    setBackdateMember(null);
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function prepareBackdatedRedemption(member: AdminMember) {
    if (!member.starts_at || !member.expires_at) {
      setNotice(
        "This Coffee Pass does not have a valid start and expiry time.",
      );
      return;
    }
    const earliest = new Date(member.starts_at).getTime() + 60_000;
    const latest = Math.min(
      Date.now() - 60_000,
      new Date(member.expires_at).getTime() - 60_000,
    );
    if (latest < earliest) {
      setNotice("There is no valid past time available for this Coffee Pass.");
      return;
    }
    const suggested = Math.max(
      earliest,
      Math.min(Date.now() - 3_600_000, latest),
    );
    setBackdateMember(member);
    setBackdateDrinkCode("");
    setBackdateOccurredAt(formatIndiaDateTimeInput(suggested));
    setBackdateMaximum(formatIndiaDateTimeInput(latest));
    setBackdateUpgrade(false);
    setEditingMember(null);
    setSurpriseMember(null);
    setShowAdd(false);
    setActivationMember(null);
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  async function exportCsv() {
    if (!supabase || !session) return;
    setExportingClients(true);
    setNotice("");
    const exportMembers: AdminMember[] = [];
    try {
      const pageSize = 1000;
      for (let page = 0; ; page += 1) {
        const { data, error } = await supabase
          .from("memberships")
          .select(
            "id,profile_id,membership_code,status,starts_at,expires_at,membership_sequence,returning_member,profiles:profiles!memberships_profile_id_fkey(full_name,phone_e164,office_id,offices(name)),subscription_plans(id,name,price,is_unlimited),membership_balances(purchased,used,remaining),membership_surprises(id,appetizer_code,appetizer_name,unlock_redemption_number,status,claimed_at)",
          )
          .order("created_at", { ascending: false })
          .range(page * pageSize, page * pageSize + pageSize - 1);
        if (error) throw error;
        const pageRows = (data || []) as unknown as AdminMember[];
        exportMembers.push(...pageRows);
        if (pageRows.length < pageSize) break;
      }
    } catch (error) {
      setNotice(
        error instanceof Error
          ? `Could not create the client spreadsheet: ${error.message}`
          : "Could not create the client spreadsheet.",
      );
      setExportingClients(false);
      return;
    }
    if (!exportMembers.length) {
      setNotice("There are no client records to export yet.");
      setExportingClients(false);
      return;
    }
    const clients = new Map<
      string,
      { latest: AdminMember; membershipCount: number }
    >();
    exportMembers.forEach((member) => {
      const existing = clients.get(member.profile_id);
      if (existing) existing.membershipCount += 1;
      else
        clients.set(member.profile_id, { latest: member, membershipCount: 1 });
    });
    const rows = [
      [
        "Name",
        "Office",
        "Mobile number",
        "Plan",
        "Status",
        "Membership ID",
        "Memberships recorded",
        "Returning member",
        "Coffees purchased",
        "Coffees used",
        "Remaining",
        "Start date",
        "Expiry date",
        "Surprise",
        "Surprise coffee number",
        "Surprise status",
      ],
      ...Array.from(clients.values()).map(({ latest, membershipCount }) => {
        const balance = embeddedOne(latest.membership_balances);
        const surprise = embeddedOne(latest.membership_surprises);
        return [
          latest.profiles.full_name || "",
          embeddedOne(latest.profiles.offices)?.name || "",
          latest.profiles.phone_e164 || "",
          latest.subscription_plans.name,
          latest.starts_at && latest.status === "pending"
            ? "queued"
            : latest.status,
          latest.membership_code,
          String(membershipCount),
          latest.returning_member || membershipCount > 1 ? "Yes" : "No",
          String(balance?.purchased ?? 0),
          String(balance?.used ?? 0),
          String(balance?.remaining ?? 0),
          latest.starts_at?.slice(0, 10) || "",
          latest.expires_at?.slice(0, 10) || "",
          surprise?.appetizer_name || "",
          String(surprise?.unlock_redemption_number ?? ""),
          surprise?.status || "",
        ];
      }),
    ];
    const safeSpreadsheetCell = (value: string) => {
      const protectedValue = /^[=+\-@]/.test(value) ? `'${value}` : value;
      return `"${protectedValue.replaceAll('"', '""')}"`;
    };
    const csv = rows
      .map((row) => row.map(safeSpreadsheetCell).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(
      new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `solo-cafe-clients-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setNotice(
      `Client spreadsheet downloaded with ${clients.size} unique ${clients.size === 1 ? "client" : "clients"}.`,
    );
    setExportingClients(false);
  }
  async function addMember(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const phone = String(form.get("phone") || "").replace(/\D/g, "");
    const selectedPlan = plans.find((plan) => plan.id === form.get("planId"));
    if (!selectedPlan || phone.length !== 10) {
      setNotice("Choose a plan and enter a valid 10-digit mobile number.");
      return;
    }
    const appetizer = surpriseAppetizers.find(
      (item) => item.id === form.get("surpriseAppetizer"),
    );
    const unlockRedemptionNumber = Number(form.get("surpriseRedemption"));
    if (
      onboardingSurprise &&
      (!appetizer ||
        !Number.isInteger(unlockRedemptionNumber) ||
        unlockRedemptionNumber < 1)
    ) {
      setNotice(
        "Choose an appetizer and a redemption number for the surprise.",
      );
      return;
    }
    setSavingMember(true);
    const ok = await api({
      action: "createAndActivate",
      name: form.get("name"),
      phone: `+91${phone}`,
      office: form.get("office"),
      planId: selectedPlan.id,
      amount: selectedPlan.price,
      method: form.get("method"),
      reference: form.get("reference"),
      surprise:
        onboardingSurprise && appetizer
          ? {
              appetizerCode: appetizer.id,
              appetizerName: appetizer.name,
              unlockRedemptionNumber,
            }
          : null,
    });
    setSavingMember(false);
    if (ok) {
      formElement.reset();
      setShowAdd(false);
      setActivationMember(null);
      setOnboardingSurprise(false);
      setStatusFilter("active");
    }
  }
  async function updateMemberProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingMember) return;
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") || "").trim();
    if (name.length < 2) {
      setNotice("Enter a customer name with at least 2 characters.");
      return;
    }
    setSavingMember(true);
    const ok = await api({
      action: "updateProfile",
      profileId: editingMember.profile_id,
      name,
      office: String(form.get("office") || "").trim(),
    });
    setSavingMember(false);
    if (ok) setEditingMember(null);
  }
  async function scheduleSurprise(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!surpriseMember) return;
    const form = new FormData(event.currentTarget);
    const appetizer = surpriseAppetizers.find(
      (item) => item.id === form.get("appetizer"),
    );
    const unlockRedemptionNumber = Number(form.get("unlockRedemptionNumber"));
    if (!appetizer || !Number.isInteger(unlockRedemptionNumber)) {
      setNotice("Choose an appetizer and redemption number.");
      return;
    }
    setSavingMember(true);
    const ok = await api({
      action: "scheduleSurprise",
      membershipId: surpriseMember.id,
      appetizerCode: appetizer.id,
      appetizerName: appetizer.name,
      unlockRedemptionNumber,
    });
    setSavingMember(false);
    if (ok) setSurpriseMember(null);
  }
  async function backdateRedemption(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!backdateMember) return;
    const form = new FormData(event.currentTarget);
    const rule = planDrinkRules.find(
      (item) =>
        item.plan_id === backdateMember.subscription_plans.id &&
        item.drink_code === backdateDrinkCode,
    );
    const occurredAt = indiaDateTimeInputToIso(backdateOccurredAt);
    const reason = String(form.get("reason") || "").trim();
    if (!rule) {
      setNotice("Choose a coffee included in this member’s plan.");
      return;
    }
    if (!occurredAt) {
      setNotice("Choose a valid date and time for the missed coffee.");
      return;
    }
    if (new Date(occurredAt).getTime() >= Date.now()) {
      setNotice("The coffee time must be earlier than the current time.");
      return;
    }
    if (reason.length < 5) {
      setNotice("Enter a reason of at least 5 characters.");
      return;
    }
    if (rule.upgrade_allowed && !backdateUpgrade) {
      setNotice("Confirm that the ₹49 Signature upgrade was collected.");
      return;
    }
    setSavingMember(true);
    const ok = await api({
      action: "backdateRedemption",
      membershipId: backdateMember.id,
      drinkCode: rule.drink_code,
      upgrade: rule.upgrade_allowed && backdateUpgrade,
      occurredAt,
      reason,
      idempotencyKey: crypto.randomUUID(),
    });
    setSavingMember(false);
    if (ok) {
      const memberName =
        backdateMember.profiles.full_name || backdateMember.membership_code;
      setBackdateMember(null);
      setBackdateDrinkCode("");
      setBackdateUpgrade(false);
      setNotice(
        `${rule.drink_name} was added to ${memberName} at ${formatIndiaDateTime(occurredAt)}.`,
      );
    }
  }
  async function assignRole(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setSavingMember(true);
    const ok = await api({
      action: "role",
      identifier: form.get("identifier"),
      role: form.get("role"),
      active: true,
      sendInvite: form.get("sendInvite") === "on",
    });
    setSavingMember(false);
    if (ok) formElement.reset();
  }
  async function changeRole(
    staffMember: AdminStaff,
    role: "staff" | "admin",
    active: boolean,
  ) {
    const identifier =
      staffMember.profiles.email || staffMember.profiles.phone_e164;
    if (!identifier) {
      setNotice("This staff account has no email address or mobile number.");
      return;
    }
    setSavingMember(true);
    await api({ action: "role", identifier, role, active });
    setSavingMember(false);
  }
  async function addTickerCount(
    event: FormEvent<HTMLFormElement>,
    itemCode: string,
  ) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const amount = Number(new FormData(formElement).get("amount"));
    if (!Number.isInteger(amount) || amount < 1 || amount > 100000) {
      setNotice("Enter a whole number between 1 and 100,000.");
      return;
    }
    setSavingMember(true);
    const ok = await api({ action: "addTickerCount", itemCode, amount });
    setSavingMember(false);
    if (ok) formElement.reset();
  }
  async function cancelPayment(payment: AdminPayment) {
    const reason = window.prompt(
      "Explain why this payment is being cancelled. The membership will not be changed automatically.",
    );
    if (reason === null) return;
    if (reason.trim().length < 5) {
      setNotice("Enter an explanation of at least 5 characters.");
      return;
    }
    setSavingMember(true);
    await api({
      action: "cancelPayment",
      paymentId: payment.id,
      reason: reason.trim(),
    });
    setSavingMember(false);
  }
  const surpriseBalance = surpriseMember
    ? embeddedOne(surpriseMember.membership_balances)
    : undefined;
  const scheduledSurprise = surpriseMember
    ? embeddedOne(surpriseMember.membership_surprises)
    : undefined;
  const eligibleSurpriseRedemptions = surpriseBalance
    ? Array.from(
        {
          length: Math.max(surpriseBalance.purchased - surpriseBalance.used, 0),
        },
        (_, index) => surpriseBalance.used + index + 1,
      )
    : [];
  const backdateDrinkRules = backdateMember
    ? planDrinkRules.filter(
        (rule) => rule.plan_id === backdateMember.subscription_plans.id,
      )
    : [];
  const selectedBackdateRule = backdateDrinkRules.find(
    (rule) => rule.drink_code === backdateDrinkCode,
  );
  const backdateMinimum = backdateMember?.starts_at
    ? formatIndiaDateTimeInput(backdateMember.starts_at)
    : undefined;
  if (!session)
    return (
      <div className="dashboard-shell">
        <SignInPanel
          title="Secure admin access"
          method="email"
          redirectPath="/admin"
          allowPassword
        />
      </div>
    );
  if (authorized === false)
    return (
      <div className="dashboard-shell">
        <div className="empty-membership">
          <ShieldCheck />
          <h2>Admin access required.</h2>
          <p>
            This account does not have the admin role. Sign out and use the
            approved administrator email.
          </p>
          <button
            className="button button--red"
            onClick={() => supabase?.auth.signOut()}
          >
            Sign out and use admin email
          </button>
        </div>
      </div>
    );
  const cards = [
    ["Active members", metrics.active, UserRound],
    ["Redeemed today", metrics.today, Coffee],
    ["Pending approvals", metrics.pending, Clock3],
    ["Queued renewals", metrics.queued, Clock3],
    ["Near expiry", metrics.expiring, Sparkles],
    ["Lapsed members", metrics.lapsed, History],
  ] as const;
  return (
    <div className="admin-page">
      <aside className="dashboard-nav">
        <Logo light />
        <p>Operations</p>
        <button
          className={`nav-button ${adminSection === "overview" ? "active" : ""}`}
          onClick={() => setAdminSection("overview")}
        >
          <LayoutDashboard /> Overview
        </button>
        <NavLink to="/staff">
          <Coffee /> Staff scanner
        </NavLink>
        <button
          className={`nav-button ${adminSection === "companies" ? "active" : ""}`}
          onClick={() => setAdminSection("companies")}
        >
          <Building2 /> Solo at Work
        </button>
        <button
          className={`nav-button ${adminSection === "payments" ? "active" : ""}`}
          onClick={() => setAdminSection("payments")}
        >
          <WalletCards /> Payments
        </button>
        <button
          className={`nav-button ${adminSection === "ticker" ? "active" : ""}`}
          onClick={() => setAdminSection("ticker")}
        >
          <Sparkles /> Order ticker
        </button>
        <button
          className={`nav-button ${adminSection === "insights" ? "active" : ""}`}
          onClick={() => setAdminSection("insights")}
        >
          <History /> Website insights
        </button>
        {isOwner && (
          <button
            className={`nav-button ${adminSection === "roles" ? "active" : ""}`}
            onClick={() => setAdminSection("roles")}
          >
            <ShieldCheck /> Staff & roles
          </button>
        )}
      </aside>
      <section className="dashboard-main">
        <div className="dashboard-title">
          <div>
            <p className="eyebrow">Solo Café admin</p>
            <h1>
              {adminSection === "overview"
                ? "Members at a glance"
                : adminSection === "companies"
                  ? "Companies and employee passes"
                  : adminSection === "payments"
                    ? "Payment ledger"
                    : adminSection === "ticker"
                      ? "Most-ordered ticker"
                      : adminSection === "insights"
                        ? "Where visitors spend time"
                        : "Staff permissions"}
            </h1>
          </div>
          {adminSection === "overview" && (
            <div className="button-row">
              <button
                className="button button--outline"
                onClick={exportCsv}
                disabled={loadingMembers || exportingClients}
              >
                <Download size={17} />{" "}
                {exportingClients
                  ? "Preparing spreadsheet…"
                  : "Download client spreadsheet"}
              </button>
              <button
                className="button button--red"
                onClick={() => {
                  setActivationMember(null);
                  setEditingMember(null);
                  setSurpriseMember(null);
                  setBackdateMember(null);
                  setOnboardingSurprise(false);
                  setShowAdd((value) => !value);
                }}
              >
                <Plus size={17} /> Add member
              </button>
            </div>
          )}
        </div>
        {notice && <p className="admin-notice">{notice}</p>}
        {adminSection === "overview" && (
          <>
            {showAdd && (
              <form
                key={activationMember?.id || "new-member"}
                className="add-member-form"
                onSubmit={addMember}
              >
                <div className="activation-form-title">
                  <p className="eyebrow">
                    {activationQueuesRenewal
                      ? "Next Coffee Pass"
                      : activationMember
                        ? "Pending signup"
                        : "OTP-verified customer"}
                  </p>
                  <strong>
                    {activationQueuesRenewal
                      ? "Verify payment and queue the next plan"
                      : activationMember
                        ? "Verify this customer’s payment and activate the pass"
                        : "Activate an existing customer after OTP signup"}
                  </strong>
                </div>
                <label>
                  Name
                  <input
                    name="name"
                    defaultValue={activationMember?.profiles.full_name || ""}
                    required
                    minLength={2}
                  />
                </label>
                <label>
                  Indian mobile
                  <input
                    name="phone"
                    defaultValue={
                      activationMember?.profiles.phone_e164
                        ?.replace(/\D/g, "")
                        .slice(-10) || ""
                    }
                    inputMode="numeric"
                    required
                    maxLength={10}
                  />
                </label>
                <label>
                  Office (optional)
                  <input
                    name="office"
                    list="admin-onboarding-office-suggestions"
                    defaultValue={
                      embeddedOne(activationMember?.profiles.offices)?.name ||
                      ""
                    }
                    maxLength={120}
                    autoComplete="organization"
                    placeholder="Company or office"
                  />
                  <datalist id="admin-onboarding-office-suggestions">
                    {offices.map((officeItem) => (
                      <option key={officeItem.id} value={officeItem.name} />
                    ))}
                  </datalist>
                </label>
                <label>
                  Plan
                  <select
                    name="planId"
                    defaultValue={activationMember?.subscription_plans.id || ""}
                    required
                  >
                    <option value="">Select plan</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} · ₹{plan.price}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Payment
                  <select name="method">
                    <option value="upi">UPI</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label>
                  Reference
                  <input name="reference" />
                </label>
                {!activationQueuesRenewal && (
                  <label className="surprise-toggle">
                    <input
                      name="surpriseEnabled"
                      type="checkbox"
                      checked={onboardingSurprise}
                      onChange={(event) =>
                        setOnboardingSurprise(event.target.checked)
                      }
                    />
                    <span>
                      <strong>Enable one-time surprise</strong>
                      Add a free appetizer on a selected coffee redemption.
                    </span>
                  </label>
                )}
                {!activationQueuesRenewal && onboardingSurprise && (
                  <>
                    <label>
                      Surprise appetizer
                      <select name="surpriseAppetizer" required>
                        <option value="">Select appetizer</option>
                        {surpriseAppetizers.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Unlock on coffee
                      <select
                        name="surpriseRedemption"
                        defaultValue="6"
                        required
                      >
                        {Array.from(
                          { length: 12 },
                          (_, index) => index + 1,
                        ).map((number) => (
                          <option key={number} value={number}>
                            Coffee #{number}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
                <button
                  className="button button--red"
                  type="submit"
                  disabled={savingMember}
                >
                  {savingMember
                    ? activationQueuesRenewal
                      ? "Queueing…"
                      : "Activating…"
                    : activationQueuesRenewal
                      ? "Verify payment & queue next plan"
                      : "Verify payment & activate"}
                </button>
              </form>
            )}
            {editingMember && (
              <form
                key={`edit-${editingMember.profile_id}`}
                className="add-member-form"
                onSubmit={updateMemberProfile}
              >
                <div className="activation-form-title">
                  <p className="eyebrow">Edit enrolled customer</p>
                  <strong>
                    Update the profile linked to{" "}
                    {editingMember.profiles.phone_e164 || "this member"}
                  </strong>
                </div>
                <label>
                  Full name
                  <input
                    name="name"
                    defaultValue={editingMember.profiles.full_name || ""}
                    required
                    minLength={2}
                    maxLength={80}
                  />
                </label>
                <label>
                  Office (optional)
                  <input
                    name="office"
                    list="admin-edit-office-suggestions"
                    defaultValue={
                      embeddedOne(editingMember.profiles.offices)?.name || ""
                    }
                    maxLength={120}
                    autoComplete="organization"
                    placeholder="Leave blank to clear"
                  />
                  <datalist id="admin-edit-office-suggestions">
                    {offices.map((officeItem) => (
                      <option key={officeItem.id} value={officeItem.name} />
                    ))}
                  </datalist>
                </label>
                <div className="button-row">
                  <button
                    className="button button--red"
                    type="submit"
                    disabled={savingMember}
                  >
                    {savingMember ? "Saving…" : "Save profile"}
                  </button>
                  <button
                    className="button button--outline"
                    type="button"
                    onClick={() => setEditingMember(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
            {surpriseMember && (
              <form
                key={`surprise-${surpriseMember.id}`}
                className="add-member-form surprise-form"
                onSubmit={scheduleSurprise}
              >
                <div className="activation-form-title">
                  <p className="eyebrow">One-time member surprise</p>
                  <strong>
                    {scheduledSurprise ? "Update" : "Schedule"} a free appetizer
                    for {surpriseMember.profiles.full_name || "this member"}
                  </strong>
                  <span>
                    {surpriseBalance?.used || 0} coffees already redeemed. The
                    member sees only the countdown until the surprise is due.
                  </span>
                </div>
                <label>
                  Appetizer
                  <select
                    name="appetizer"
                    defaultValue={scheduledSurprise?.appetizer_code || ""}
                    required
                  >
                    <option value="">Select appetizer</option>
                    {surpriseAppetizers.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Surprise redemption
                  <select
                    name="unlockRedemptionNumber"
                    defaultValue={
                      scheduledSurprise?.unlock_redemption_number ||
                      eligibleSurpriseRedemptions[
                        Math.floor(eligibleSurpriseRedemptions.length / 2)
                      ] ||
                      ""
                    }
                    required
                  >
                    {eligibleSurpriseRedemptions.map((number) => (
                      <option key={number} value={number}>
                        Coffee #{number} · in{" "}
                        {number - (surpriseBalance?.used || 0)}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  className="button button--red"
                  type="submit"
                  disabled={savingMember || !eligibleSurpriseRedemptions.length}
                >
                  {savingMember ? "Saving…" : "Save surprise"}
                </button>
                <button
                  className="button button--outline"
                  type="button"
                  onClick={() => setSurpriseMember(null)}
                >
                  Cancel
                </button>
              </form>
            )}
            {backdateMember && (
              <form
                key={`backdate-${backdateMember.id}`}
                className="add-member-form backdate-form"
                onSubmit={backdateRedemption}
              >
                <div className="activation-form-title">
                  <strong>
                    Record a missed coffee for{" "}
                    {backdateMember.profiles.full_name ||
                      backdateMember.membership_code}
                  </strong>
                  <span id="backdate-help">
                    Use the time the coffee was actually served. This deducts
                    one coffee and adds an audited entry to the member’s
                    history.
                  </span>
                </div>
                <label>
                  Served on
                  <input
                    name="occurredAt"
                    type="datetime-local"
                    value={backdateOccurredAt}
                    min={backdateMinimum}
                    max={backdateMaximum}
                    step="60"
                    onChange={(event) =>
                      setBackdateOccurredAt(event.target.value)
                    }
                    aria-describedby="backdate-help"
                    required
                  />
                </label>
                <label>
                  Coffee served
                  <select
                    name="drinkCode"
                    value={backdateDrinkCode}
                    onChange={(event) => {
                      setBackdateDrinkCode(event.target.value);
                      setBackdateUpgrade(false);
                    }}
                    required
                  >
                    <option value="">Choose coffee</option>
                    {backdateDrinkRules.map((rule) => (
                      <option key={rule.drink_code} value={rule.drink_code}>
                        {rule.drink_name}
                        {rule.upgrade_allowed
                          ? ` · ₹${rule.upgrade_price ?? 49} upgrade`
                          : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="backdate-reason">
                  Why was it missed?
                  <textarea
                    name="reason"
                    minLength={5}
                    maxLength={300}
                    placeholder="Example: Served at the counter but not punched"
                    required
                  />
                </label>
                {selectedBackdateRule?.upgrade_allowed && (
                  <label className="surprise-toggle backdate-upgrade">
                    <input
                      name="upgradeCollected"
                      type="checkbox"
                      checked={backdateUpgrade}
                      onChange={(event) =>
                        setBackdateUpgrade(event.target.checked)
                      }
                      required
                    />
                    <span>
                      <strong>
                        ₹{selectedBackdateRule.upgrade_price ?? 49} Signature
                        upgrade collected
                      </strong>
                      Confirm the customer paid the upgrade at the counter.
                    </span>
                  </label>
                )}
                <div className="button-row backdate-actions">
                  <button
                    className="button button--red"
                    type="submit"
                    disabled={savingMember || !backdateDrinkCode}
                  >
                    {savingMember ? "Recording…" : "Record past coffee"}
                  </button>
                  <button
                    className="button button--outline"
                    type="button"
                    onClick={() => setBackdateMember(null)}
                    disabled={savingMember}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
            <div className="metric-grid">
              {cards.map(([label, value, Icon]) => (
                <article key={label}>
                  <Icon />
                  <p>{label}</p>
                  <strong>{value}</strong>
                  <small>Live from Supabase</small>
                </article>
              ))}
            </div>
            <section className="admin-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Signups and members</p>
                  <h2>Search by name, number, office or member ID</h2>
                </div>
                <label className="search-box">
                  <Search size={20} />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Name, phone, office or member ID"
                  />
                </label>
              </div>
              <div className="category-pills admin-filters">
                <button
                  type="button"
                  className={statusFilter === "pending" ? "active" : ""}
                  onClick={() => setStatusFilter("pending")}
                >
                  Pending ({metrics.pending})
                </button>
                <button
                  type="button"
                  className={statusFilter === "queued" ? "active" : ""}
                  onClick={() => setStatusFilter("queued")}
                >
                  Queued ({metrics.queued})
                </button>
                <button
                  type="button"
                  className={statusFilter === "active" ? "active" : ""}
                  onClick={() => setStatusFilter("active")}
                >
                  Active ({metrics.active})
                </button>
                <button
                  type="button"
                  className={statusFilter === "paused" ? "active" : ""}
                  onClick={() => setStatusFilter("paused")}
                >
                  Paused
                </button>
                <button
                  type="button"
                  className={statusFilter === "lapsed" ? "active" : ""}
                  onClick={() => setStatusFilter("lapsed")}
                >
                  Lapsed ({metrics.lapsed})
                </button>
                <button
                  type="button"
                  className={statusFilter === "all" ? "active" : ""}
                  onClick={() => setStatusFilter("all")}
                >
                  All
                </button>
              </div>
              {loadingMembers ? (
                <div className="empty-state">
                  <Clock3 />
                  <h3>Loading signups…</h3>
                </div>
              ) : filtered.length ? (
                <div className="member-table">
                  {filtered.map((member) => (
                    <article className="member-row" key={member.id}>
                      <div>
                        <strong>
                          {member.profiles.full_name || "Name not provided"}
                        </strong>
                        <span>
                          {member.profiles.phone_e164 || "No phone"} ·{" "}
                          {member.membership_code}
                        </span>
                        <span>
                          Office ·{" "}
                          {embeddedOne(member.profiles.offices)?.name ||
                            "Not provided"}
                        </span>
                        <span>
                          Requested · {formatIndiaDateTime(member.created_at)}
                        </span>
                        {member.returning_member && (
                          <span className="returning-member-note">
                            Returning member · pass #
                            {member.membership_sequence}
                          </span>
                        )}
                      </div>
                      <div>
                        <strong>{member.subscription_plans.name}</strong>
                        <span>
                          {member.status === "pending"
                            ? member.starts_at
                              ? `Paid · starts ${new Date(member.starts_at).toLocaleDateString("en-IN")}`
                              : "Awaiting payment"
                            : `${embeddedOne(member.membership_balances)?.remaining || 0} remaining`}
                        </span>
                        {embeddedOne(member.membership_surprises) && (
                          <span className="surprise-admin-note">
                            ✦{" "}
                            {
                              embeddedOne(member.membership_surprises)
                                ?.appetizer_name
                            }
                            {embeddedOne(member.membership_surprises)
                              ?.status === "claimed"
                              ? " · claimed"
                              : ` · coffee #${embeddedOne(member.membership_surprises)?.unlock_redemption_number}`}
                          </span>
                        )}
                      </div>
                      <span
                        className={`status status--${member.starts_at && member.status === "pending" ? "queued" : member.status}`}
                      >
                        {member.starts_at && member.status === "pending"
                          ? "queued"
                          : member.status}
                      </span>
                      <div className="member-actions">
                        <button
                          type="button"
                          onClick={() => prepareProfileEdit(member)}
                        >
                          Edit profile
                        </button>
                        {["active", "expired"].includes(member.status) &&
                          member.starts_at &&
                          member.expires_at &&
                          (member.subscription_plans.is_unlimited ||
                            (embeddedOne(member.membership_balances)
                              ?.remaining ?? 0) > 0) && (
                            <button
                              type="button"
                              onClick={() => prepareBackdatedRedemption(member)}
                            >
                              Add past coffee
                            </button>
                          )}
                        {member.status === "active" &&
                          embeddedOne(member.membership_surprises)?.status !==
                            "claimed" && (
                            <button
                              type="button"
                              onClick={() => prepareSurprise(member)}
                            >
                              {embeddedOne(member.membership_surprises)
                                ? "Edit surprise"
                                : "Add surprise"}
                            </button>
                          )}
                        {member.status === "pending" && !member.starts_at ? (
                          <button
                            type="button"
                            onClick={() => prepareActivation(member)}
                          >
                            Verify payment
                          </button>
                        ) : member.status === "active" ? (
                          <>
                            <button
                              type="button"
                              onClick={() => prepareActivation(member)}
                            >
                              Queue next plan
                            </button>
                            <button
                              type="button"
                              onClick={() => changeStatus(member.id, "paused")}
                            >
                              Pause
                            </button>
                          </>
                        ) : member.status === "paused" ? (
                          <button
                            type="button"
                            onClick={() => changeStatus(member.id, "active")}
                          >
                            Resume
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => changeStatus(member.id, "cancelled")}
                        >
                          Cancel
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <ShieldCheck />
                  <h3>No matching member records.</h3>
                  <p>Try another number or choose a different status.</p>
                </div>
              )}
            </section>
          </>
        )}
        {adminSection === "companies" && (
          <div className="corporate-admin">
            <div className="corporate-admin__forms">
              <form
                className="admin-panel corporate-admin__form"
                onSubmit={createCompany}
              >
                <div className="panel-heading">
                  <div>
                    <p className="eyebrow">New company</p>
                    <h2>Create a workplace plan</h2>
                  </div>
                </div>
                <label>
                  Company name
                  <input
                    name="companyName"
                    required
                    minLength={2}
                    maxLength={160}
                  />
                </label>
                <label>
                  Office location
                  <input
                    name="officeLocation"
                    required
                    minLength={2}
                    maxLength={160}
                  />
                </label>
                <label>
                  Employee tier
                  <select name="employeeTier" defaultValue="50">
                    <option value="10">10 employees</option>
                    <option value="50">50 employees</option>
                    <option value="100">100 employees</option>
                    <option value="200">200 employees</option>
                    <option value="500">500 employees</option>
                  </select>
                </label>
                <label>
                  Monthly bill (₹)
                  <input
                    name="monthlyBill"
                    type="number"
                    min="0"
                    required
                    defaultValue="54950"
                  />
                </label>
                <label>
                  Start date
                  <input name="startsOn" type="date" />
                </label>
                <label>
                  Expiry date
                  <input name="expiresOn" type="date" />
                </label>
                <button
                  className="button button--red"
                  type="submit"
                  disabled={savingMember}
                >
                  {savingMember ? "Saving…" : "Create company"}
                </button>
              </form>
              <form
                className="admin-panel corporate-admin__form"
                onSubmit={importCorporateEmployees}
              >
                <div className="panel-heading">
                  <div>
                    <p className="eyebrow">Employee list</p>
                    <h2>Add or import employees</h2>
                  </div>
                </div>
                <label>
                  Company
                  <select
                    name="companyId"
                    required
                    defaultValue={selectedCompanyId || ""}
                    onChange={(event) =>
                      setSelectedCompanyId(Number(event.target.value) || null)
                    }
                  >
                    <option value="">Select company</option>
                    {corporateCompanies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name} · {company.office_location}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  One employee per line
                  <textarea
                    name="employees"
                    rows={7}
                    required
                    placeholder={
                      "Aarav Shah, 9876543210, aarav@company.com\nRiya Mehta, 9988776655"
                    }
                  />
                </label>
                <small>
                  Format: Full name, mobile number, email (optional).
                  Re-importing the same mobile updates that employee instead of
                  creating a duplicate.
                </small>
                <button
                  className="button button--red"
                  type="submit"
                  disabled={savingMember}
                >
                  {savingMember ? "Importing…" : "Add employee list"}
                </button>
              </form>
            </div>

            <section className="admin-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Company accounts</p>
                  <h2>Plans, billing and usage</h2>
                </div>
              </div>
              {corporateCompanies.length ? (
                <div className="company-list">
                  {corporateCompanies.map((company) => {
                    const usage = companyUsage.get(company.id);
                    const employeeCount = corporateEmployees.filter(
                      (employee) =>
                        employee.company_id === company.id && employee.active,
                    ).length;
                    return (
                      <article
                        className={`company-row ${selectedCompanyId === company.id ? "active" : ""}`}
                        key={company.id}
                      >
                        <button
                          type="button"
                          className="company-row__main"
                          onClick={() => setSelectedCompanyId(company.id)}
                        >
                          <div>
                            <strong>{company.name}</strong>
                            <span>
                              {company.office_location} ·{" "}
                              {company.employee_tier} employee tier
                            </span>
                          </div>
                          <div>
                            <strong>
                              ₹{company.monthly_bill.toLocaleString("en-IN")}
                            </strong>
                            <span>Monthly bill</span>
                          </div>
                          <div>
                            <strong>
                              {usage?.active_employees ?? employeeCount}
                            </strong>
                            <span>Active employees</span>
                          </div>
                          <div>
                            <strong>{usage?.coffees_redeemed ?? 0}</strong>
                            <span>Coffees redeemed</span>
                          </div>
                          <span
                            className={`status status--${company.status === "active" ? "active" : "paused"}`}
                          >
                            {company.status}
                          </span>
                        </button>
                        <div className="member-actions">
                          <button
                            type="button"
                            onClick={() => renewCompany(company)}
                          >
                            Renew cycle
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setCompanyStatus(
                                company,
                                company.status === "active"
                                  ? "inactive"
                                  : "active",
                              )
                            }
                          >
                            {company.status === "active"
                              ? "Deactivate"
                              : "Activate"}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <Building2 />
                  <h3>No companies added yet.</h3>
                  <p>Create the first Solo at Work account above.</p>
                </div>
              )}
            </section>

            {selectedCompany && (
              <section className="admin-panel">
                <div className="panel-heading">
                  <div>
                    <p className="eyebrow">{selectedCompany.name}</p>
                    <h2>Covered employees</h2>
                  </div>
                  <span>
                    {selectedCompany.starts_on || "Start not set"} to{" "}
                    {selectedCompany.expires_on || "Expiry not set"}
                  </span>
                </div>
                {selectedCompanyEmployees.length ? (
                  <div className="member-table">
                    {selectedCompanyEmployees.map((employee) => (
                      <article className="member-row" key={employee.id}>
                        <div>
                          <strong>{employee.full_name}</strong>
                          <span>
                            {employee.phone_e164}
                            {employee.email ? ` · ${employee.email}` : ""}
                          </span>
                        </div>
                        <div>
                          <strong>
                            {employee.profile_id
                              ? "Pass account linked"
                              : "Awaiting member signup"}
                          </strong>
                          <span>
                            {employee.membership_starts_on || "No start"} to{" "}
                            {employee.membership_expires_on || "No expiry"}
                          </span>
                        </div>
                        <span
                          className={`status status--${employee.active ? "active" : "paused"}`}
                        >
                          {employee.active ? "active" : "inactive"}
                        </span>
                        <div className="member-actions">
                          <button
                            type="button"
                            onClick={() =>
                              api({
                                action: "setCorporateEmployee",
                                employeeId: employee.id,
                                active: !employee.active,
                              })
                            }
                          >
                            {employee.active ? "Deactivate" : "Reactivate"}
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <UserRound />
                    <h3>No employees in this company yet.</h3>
                  </div>
                )}
              </section>
            )}

            <section className="admin-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Website enquiries</p>
                  <h2>Companies interested in Solo at Work</h2>
                </div>
              </div>
              {corporateEnquiries.length ? (
                <div className="company-list">
                  {corporateEnquiries.map((enquiry) => (
                    <article
                      className="company-row corporate-enquiry-row"
                      key={enquiry.id}
                    >
                      <div>
                        <strong>{enquiry.company_name}</strong>
                        <span>
                          {enquiry.office_location} ·{" "}
                          {enquiry.employees_covered} employees
                        </span>
                      </div>
                      <div>
                        <strong>{enquiry.contact_person}</strong>
                        <span>
                          {enquiry.designation} · {enquiry.work_email} ·{" "}
                          {enquiry.mobile_number}
                        </span>
                      </div>
                      <div>
                        <strong>
                          {enquiry.preferred_start_date || "Start date open"}
                        </strong>
                        <span>
                          {new Date(enquiry.created_at).toLocaleDateString(
                            "en-IN",
                          )}
                        </span>
                      </div>
                      <select
                        aria-label={`Status for ${enquiry.company_name}`}
                        value={enquiry.status}
                        onChange={(event) =>
                          api({
                            action: "setCorporateEnquiryStatus",
                            enquiryId: enquiry.id,
                            status: event.target.value,
                          })
                        }
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Building2 />
                  <h3>No workplace enquiries yet.</h3>
                </div>
              )}
            </section>
          </div>
        )}
        {adminSection === "ticker" && (
          <section className="admin-panel ticker-admin">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Live website activity</p>
                <h2>Most-ordered item counts</h2>
              </div>
              <p className="ticker-admin__schedule">
                One random item receives +4 or +5 every 5 minutes while the café
                is open.
              </p>
            </div>
            {loadingMembers ? (
              <div className="empty-state">
                <Clock3 />
                <h3>Loading ticker counts…</h3>
              </div>
            ) : mostOrderedCounts.length ? (
              <div className="ticker-admin__list">
                {mostOrderedCounts.map((item) => (
                  <article className="ticker-admin__row" key={item.item_code}>
                    <span className="ticker-admin__rank">
                      {String(item.display_rank).padStart(2, "0")}
                    </span>
                    <div className="ticker-admin__item">
                      <strong>{item.item_name}</strong>
                      <span>
                        {item.category} · Updated{" "}
                        {new Date(item.updated_at).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <strong className="ticker-admin__count">
                      {item.total_count.toLocaleString("en-IN")}
                    </strong>
                    <form
                      className="ticker-admin__form"
                      onSubmit={(event) =>
                        addTickerCount(event, item.item_code)
                      }
                    >
                      <label>
                        <span className="sr-only">
                          Amount to add to {item.item_name}
                        </span>
                        <input
                          name="amount"
                          type="number"
                          min="1"
                          max="100000"
                          step="1"
                          inputMode="numeric"
                          placeholder="Add count"
                          required
                        />
                      </label>
                      <button
                        type="submit"
                        className="button button--red button--small"
                        disabled={savingMember}
                      >
                        <Plus size={15} /> Add
                      </button>
                    </form>
                  </article>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Sparkles />
                <h3>No ticker items are configured.</h3>
              </div>
            )}
          </section>
        )}
        {adminSection === "insights" && (
          <section className="admin-panel engagement-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Last 30 days · privacy conscious</p>
                <h2>Active attention by page</h2>
              </div>
              <p className="ticker-admin__schedule">
                Counts active, visible time only. No names, phone numbers, IPs,
                query strings or typed content are stored.
              </p>
            </div>
            {websiteEngagement.length ? (
              <div className="engagement-list">
                {websiteEngagement.map((page) => {
                  const peak = Math.max(
                    ...websiteEngagement.map((item) =>
                      Number(item.total_active_seconds),
                    ),
                    1,
                  );
                  return (
                    <article className="engagement-row" key={page.path}>
                      <div>
                        <strong>{page.path}</strong>
                        <span>
                          {Number(page.visits).toLocaleString("en-IN")}{" "}
                          anonymous visits
                        </span>
                      </div>
                      <div className="engagement-bar" aria-hidden="true">
                        <span
                          style={{
                            width: `${Math.max((Number(page.total_active_seconds) / peak) * 100, 3)}%`,
                          }}
                        />
                      </div>
                      <div>
                        <strong>
                          {Math.round(Number(page.average_active_seconds))}s avg
                        </strong>
                        <span>
                          {Math.round(Number(page.total_active_seconds) / 60)}{" "}
                          active minutes
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <Clock3 />
                <h3>Engagement tracking starts now.</h3>
                <p>
                  Page insights will appear after visitors spend active time on
                  the website.
                </p>
              </div>
            )}
          </section>
        )}
        {adminSection === "payments" && (
          <section className="admin-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Recorded transactions</p>
                <h2>Subscriptions, renewals and upgrades</h2>
              </div>
              <label className="search-box">
                <Search size={20} />
                <input
                  value={paymentQuery}
                  onChange={(event) => setPaymentQuery(event.target.value)}
                  placeholder="Customer, phone, member ID or reference"
                />
              </label>
            </div>
            {loadingMembers ? (
              <div className="empty-state">
                <Clock3 />
                <h3>Loading payments…</h3>
              </div>
            ) : filteredPayments.length ? (
              <div className="payment-table">
                <div className="payment-row payment-row--header">
                  <span>Customer</span>
                  <span>Payment</span>
                  <span>Amount</span>
                  <span>Date</span>
                  <span>Status</span>
                  <span>Action</span>
                </div>
                {filteredPayments.map((payment) => (
                  <article className="payment-row" key={payment.id}>
                    <div>
                      <strong>
                        {payment.profiles.full_name || "Name not provided"}
                      </strong>
                      <span>
                        {payment.profiles.phone_e164 || "No phone"} ·{" "}
                        {payment.memberships?.membership_code || "No member ID"}
                      </span>
                    </div>
                    <div>
                      <strong>
                        {payment.memberships?.subscription_plans.name ||
                          payment.kind}
                      </strong>
                      <span>
                        {payment.method.toUpperCase()}
                        {payment.provider_reference
                          ? ` · ${payment.provider_reference}`
                          : ""}
                      </span>
                      {payment.cancelled_reason && (
                        <span>Cancelled: {payment.cancelled_reason}</span>
                      )}
                    </div>
                    <strong>₹{payment.amount}</strong>
                    <span>
                      {new Date(
                        payment.paid_at || payment.created_at,
                      ).toLocaleString("en-IN")}
                    </span>
                    <span className={`status status--${payment.status}`}>
                      {payment.cancelled_at ? "cancelled" : payment.status}
                    </span>
                    <div className="member-actions">
                      {payment.status === "verified" ||
                      payment.status === "pending" ? (
                        <button
                          type="button"
                          disabled={savingMember}
                          onClick={() => cancelPayment(payment)}
                        >
                          Cancel payment
                        </button>
                      ) : (
                        <span>
                          {payment.cancelled_at
                            ? new Date(payment.cancelled_at).toLocaleDateString(
                                "en-IN",
                              )
                            : "Not provided"}
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <WalletCards />
                <h3>No matching payments.</h3>
                <p>Try another customer, number or reference.</p>
              </div>
            )}
          </section>
        )}
        {adminSection === "roles" && isOwner && (
          <>
            <form className="role-form" onSubmit={assignRole}>
              <div className="activation-form-title">
                <p className="eyebrow">Grant staff access</p>
                <strong>
                  Invite a person and choose exactly what they can access
                </strong>
              </div>
              <label>
                Staff email
                <input
                  name="identifier"
                  type="email"
                  placeholder="staff@example.com"
                  required
                />
              </label>
              <label>
                Role
                <select name="role" defaultValue="staff">
                  <option value="staff">Staff · scan and redeem</option>
                  <option value="admin">Admin · full operations</option>
                </select>
              </label>
              <label className="check-row">
                <input name="sendInvite" type="checkbox" defaultChecked />
                <span>
                  Email a secure setup link so this person can create their
                  password and log in.
                </span>
              </label>
              <button
                className="button button--red"
                type="submit"
                disabled={savingMember}
              >
                {savingMember ? "Inviting…" : "Invite and assign role"}
              </button>
            </form>
            <section className="admin-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Current access</p>
                  <h2>Staff and administrators</h2>
                </div>
              </div>
              {loadingMembers ? (
                <div className="empty-state">
                  <Clock3 />
                  <h3>Loading staff…</h3>
                </div>
              ) : staff.length ? (
                <div className="member-table">
                  {staff.map((staffMember) => (
                    <article
                      className="staff-role-row"
                      key={staffMember.profile_id}
                    >
                      <div>
                        <strong>
                          {staffMember.profiles.full_name ||
                            staffMember.profiles.email ||
                            "Staff account"}
                        </strong>
                        <span>
                          {staffMember.profiles.email ||
                            staffMember.profiles.phone_e164 ||
                            "No contact identifier"}
                        </span>
                      </div>
                      <span
                        className={`status status--${staffMember.active ? "active" : "paused"}`}
                      >
                        {staffMember.active ? staffMember.role : "inactive"}
                      </span>
                      <div className="member-actions">
                        <button
                          type="button"
                          disabled={savingMember}
                          onClick={() =>
                            changeRole(
                              staffMember,
                              staffMember.role === "admin" ? "staff" : "admin",
                              true,
                            )
                          }
                        >
                          Make{" "}
                          {staffMember.role === "admin" ? "staff" : "admin"}
                        </button>
                        <button
                          type="button"
                          disabled={savingMember}
                          onClick={() =>
                            changeRole(
                              staffMember,
                              staffMember.role,
                              !staffMember.active,
                            )
                          }
                        >
                          {staffMember.active ? "Deactivate" : "Reactivate"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <ShieldCheck />
                  <h3>No staff roles yet.</h3>
                </div>
              )}
            </section>
          </>
        )}
      </section>
    </div>
  );
}

function SoloAtWorkPage() {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const tiers = [
    ["10 employees", "₹11,990 / month", "₹1,199 per employee"],
    ["50 employees", "₹54,950 / month", "₹1,099 per employee"],
    ["100 employees", "₹99,900 / month", "₹999 per employee"],
    ["200 employees", "₹1,89,800 / month", "₹949 per employee"],
    ["500 employees", "₹4,49,500 / month", "₹899 per employee"],
    ["500+ employees", "Custom plan", "Talk to Solo"],
  ];
  const included = [
    "Espresso",
    "Americano",
    "Cappuccino",
    "Latte",
    "Flat White",
    "Iced Americano",
    "Iced Latte",
    "Cold Brew",
  ];
  const upgrades = [
    "Café Mocha",
    "House Cold Coffee",
    "Iced Mocha",
    "Vietnamese Cold Brew",
    "Fridge Ciggy",
    "Sunrise Americano",
    "Coco-cano",
  ];
  async function submitEnquiry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supabase) return setMessage("Please try again in a moment.");
    if (TURNSTILE_SITE_KEY && !captchaToken)
      return setMessage("Complete the security check before sending.");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const phone = String(form.get("mobileNumber") || "").replace(/\D/g, "");
    if (!/^[6-9][0-9]{9}$/.test(phone))
      return setMessage("Enter a valid 10-digit Indian mobile number.");
    setSending(true);
    setMessage("");
    const { error } = await supabase.functions.invoke("corporate-enquiry", {
      body: {
        companyName: form.get("companyName"),
        contactPerson: form.get("contactPerson"),
        designation: form.get("designation"),
        workEmail: form.get("workEmail"),
        mobileNumber: `+91${phone}`,
        officeLocation: form.get("officeLocation"),
        employeesCovered: Number(form.get("employeesCovered")),
        preferredStartDate: form.get("preferredStartDate") || null,
        notes: form.get("notes"),
        captchaToken: captchaToken || undefined,
      },
    });
    setSending(false);
    setCaptchaToken("");
    turnstileRef.current?.reset();
    if (error)
      return setMessage(error.message || "Could not send the enquiry.");
    formElement.reset();
    setMessage("Thanks. Your workplace coffee enquiry is with the Solo team.");
  }
  return (
    <div className="page work-page">
      <section className="work-hero">
        <div>
          <p className="eyebrow eyebrow--gold">Solo at Work</p>
          <h1>Your Team’s Coffee. Covered. ☕</h1>
          <p>
            Give employees a practical workplace perk with one company plan, one
            monthly bill and a personal digital coffee pass for every covered
            team member.
          </p>
          <a className="button button--cream" href="#work-enquiry">
            Get a workplace coffee plan <ArrowRight size={18} />
          </a>
        </div>
        <aside className="work-hero__value">
          <Building2 />
          <strong>From ₹899</strong>
          <span>per employee each month</span>
          <small>Less than ₹45 per working day</small>
        </aside>
      </section>

      <section className="section-pad work-pricing">
        <SectionHeading
          eyebrow="Simple company pricing"
          title={
            <>
              Choose the size of your <em>coffee benefit.</em>
            </>
          }
        />
        <div className="work-tier-grid">
          {tiers.map(([people, price, rate]) => (
            <article key={people}>
              <Building2 />
              <h3>{people}</h3>
              <strong>{price}</strong>
              <span>{rate}</span>
            </article>
          ))}
        </div>
        <p className="work-pricing__note">
          Pricing includes up to 20 coffees per covered employee every 30 days,
          with a maximum of one included coffee per day. Unused coffees do not
          roll over.
        </p>
      </section>

      <section className="work-inclusions section-pad">
        <div>
          <p className="eyebrow eyebrow--gold">What employees receive</p>
          <h2>A coffee pass that lives in their pocket.</h2>
          <ul className="work-check-list">
            <li>
              <BadgeCheck /> Personal digital pass
            </li>
            <li>
              <Coffee /> Up to 20 coffees every 30 days
            </li>
            <li>
              <Clock3 /> Maximum one included coffee per day
            </li>
            <li>
              <ShieldCheck /> Personal and non-transferable
            </li>
          </ul>
        </div>
        <div className="work-drink-card">
          <p className="eyebrow">Included coffees</p>
          <h3>{included.join(" · ")}</h3>
          <hr />
          <p className="eyebrow">₹49 Signature upgrade</p>
          <p>{upgrades.join(" · ")}</p>
          <small>
            The employee pays ₹49 for each upgrade. Flavours and alternative
            milks are charged at menu price. Chocolate drinks are not included.
          </small>
        </div>
      </section>

      <section className="section-pad work-benefits">
        <SectionHeading
          eyebrow="Built for HR and office teams"
          title={
            <>
              Easy to offer. <em>Easy to run.</em>
            </>
          }
        />
        <div className="work-benefit-grid">
          <article>
            <WalletCards />
            <h3>One monthly bill</h3>
            <p>
              Keep the benefit predictable with company-level pricing and a
              clear billing cycle.
            </p>
          </article>
          <article>
            <UserRound />
            <h3>Employee control</h3>
            <p>
              Add, import, activate or deactivate covered employees as your team
              changes.
            </p>
          </article>
          <article>
            <LayoutDashboard />
            <h3>Useful reporting</h3>
            <p>
              See active employee count, coffees redeemed and Signature
              upgrades.
            </p>
          </article>
          <article>
            <Sparkles />
            <h3>A perk people use</h3>
            <p>
              Make everyday office coffee feel considered without adding work
              for HR.
            </p>
          </article>
        </div>
      </section>

      <section className="work-enquiry section-pad" id="work-enquiry">
        <div className="work-enquiry__intro">
          <p className="eyebrow eyebrow--gold">Bring Solo to work</p>
          <h2>Tell us about your team.</h2>
          <p>
            We will recommend the right tier and arrange onboarding with your HR
            or office team. No automatic signup or payment is taken here.
          </p>
          <a
            className="button button--outline-light"
            href={whatsappUrl(
              "Hi Solo Café, I would like to discuss Solo at Work for my company.",
            )}
            target="_blank"
            rel="noreferrer"
          >
            Enquire on WhatsApp <ArrowRight size={18} />
          </a>
        </div>
        <form className="work-enquiry__form" onSubmit={submitEnquiry}>
          <label>
            Company name
            <input name="companyName" required minLength={2} maxLength={160} />
          </label>
          <label>
            Contact person
            <input
              name="contactPerson"
              required
              minLength={2}
              maxLength={100}
            />
          </label>
          <label>
            Designation
            <input name="designation" required minLength={2} maxLength={100} />
          </label>
          <label>
            Work email
            <input name="workEmail" type="email" required maxLength={200} />
          </label>
          <label>
            Mobile number
            <input
              name="mobileNumber"
              inputMode="numeric"
              required
              maxLength={10}
              placeholder="10-digit Indian mobile"
            />
          </label>
          <label>
            Office location
            <input
              name="officeLocation"
              required
              minLength={2}
              maxLength={160}
            />
          </label>
          <label>
            Employees to cover
            <input
              name="employeesCovered"
              type="number"
              required
              min={1}
              max={100000}
            />
          </label>
          <label>
            Preferred start date
            <input name="preferredStartDate" type="date" />
          </label>
          <label className="work-enquiry__notes">
            Anything we should know?
            <textarea name="notes" maxLength={2000} rows={4} />
          </label>
          {TURNSTILE_SITE_KEY && (
            <div className="turnstile-check work-enquiry__captcha">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={setCaptchaToken}
                onExpire={() => setCaptchaToken("")}
                onError={() =>
                  setMessage(
                    "The security check could not load. Please refresh and try again.",
                  )
                }
                options={{ theme: "light", size: "flexible" }}
              />
            </div>
          )}
          {message && (
            <p className="work-enquiry__message" role="status">
              {message}
            </p>
          )}
          <button
            className="button button--red"
            type="submit"
            disabled={sending}
          >
            {sending ? "Sending…" : "Send workplace enquiry"}{" "}
            <ArrowRight size={18} />
          </button>
        </form>
      </section>
    </div>
  );
}

function AboutPage() {
  return (
    <div className="page">
      <section className="page-hero page-hero--about">
        <p className="eyebrow">Our story</p>
        <h1>
          Good food for
          <br />
          <em>the time you have.</em>
        </h1>
      </section>
      <section className="story section-pad">
        <div>
          <p className="eyebrow">Built inside the workday</p>
          <h2>Solo is the café between your meetings.</h2>
        </div>
        <div>
          <p>
            Inside Marathon Futurex, Solo Café is made for Lower Parel’s working
            crowd: people who care about their coffee, want a satisfying meal,
            and do not have an hour to find it.
          </p>
          <p>
            Our menu moves from house cold coffee to pizzas, pastas, momos,
            sandwiches and desserts, familiar favourites with a colourful Solo
            point of view.
          </p>
        </div>
      </section>
      <section className="values">
        <article>
          <Coffee />
          <h3>Café energy</h3>
          <p>Specialty coffee and a menu worth leaving your desk for.</p>
        </article>
        <article>
          <Clock3 />
          <h3>Workday pace</h3>
          <p>Clear choices and quick counter service for short breaks.</p>
        </article>
        <article>
          <Sparkles />
          <h3>Everyday delight</h3>
          <p>
            Premium enough to feel like a treat, approachable enough for every
            day.
          </p>
        </article>
      </section>
    </div>
  );
}

function VisitPage() {
  return (
    <div className="page visit-page">
      <section className="page-hero page-hero--visit">
        <p className="eyebrow">Visit Solo</p>
        <h1>
          Find your
          <br />
          <em>new break spot.</em>
        </h1>
      </section>
      <section className="visit-grid">
        <div className="visit-card">
          <MapPin />
          <p className="eyebrow">Location</p>
          <h2>Solo Café</h2>
          <p>Marathon Futurex, Lower Parel, Mumbai, Maharashtra</p>
          <a
            className="button button--red"
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
          >
            Open in Maps <ArrowRight size={18} />
          </a>
        </div>
        <div className="visit-card visit-card--red">
          <Phone />
          <p className="eyebrow eyebrow--gold">Desk delivery & enquiries</p>
          <h2 className="visit-phone-number">+91 85919 42655</h2>
          <p>
            Message Solo Café for office delivery availability, workplace
            enquiries, and Coffee Pass help.
          </p>
          <a
            className="button button--cream"
            href={whatsappUrl("Hi Solo Café, I’d like to get in touch.")}
            target="_blank"
            rel="noreferrer"
          >
            Message on WhatsApp
          </a>
        </div>
      </section>
      <section className="location-map" aria-label="Map showing Solo Café">
        <iframe
          src={MAP_EMBED_URL}
          title="Solo Café at Marathon Futurex"
          loading="lazy"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </section>
      <section className="hours-card">
        <div className="hours-card__intro">
          <Clock3 />
          <h2>Opening hours</h2>
          <p>Drop in for coffee, food or a quick workday reset.</p>
        </div>
        <dl>
          <div>
            <dt>Monday - Friday</dt>
            <dd>8:30 AM - 8:00 PM</dd>
          </div>
          <div>
            <dt>Saturday</dt>
            <dd>8:30 AM - 6:00 PM</dd>
          </div>
          <div>
            <dt>Sunday</dt>
            <dd>Closed</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    enquiryType: "General enquiry",
    message: "",
  });
  const [whatsappDraft, setWhatsappDraft] = useState("");

  function updateField(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const details = [
      "Hi Solo Café, I’d like to get in touch.",
      "",
      `Enquiry: ${form.enquiryType}`,
      `Name: ${form.name.trim()}`,
      `Company / workplace: ${form.company.trim() || "Not provided"}`,
      `Phone: ${form.phone.trim()}`,
      `Email: ${form.email.trim() || "Not provided"}`,
      "",
      form.message.trim(),
    ].join("\n");
    const draft = whatsappUrl(details);
    setWhatsappDraft(draft);
    window.open(draft, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="page contact-page">
      <section className="page-hero page-hero--contact">
        <p className="eyebrow eyebrow--gold">Talk to Solo</p>
        <h1>
          Let’s make work
          <br />
          <em>taste better.</em>
        </h1>
        <p>
          Visit us, ask about the Coffee Pass, or start a conversation about
          bringing Solo Café into your workplace.
        </p>
      </section>

      <section className="partnership-intro section-pad">
        <SectionHeading
          eyebrow="Solo for workplaces"
          title={
            <>
              Bring the café closer
              <br />
              <em>to your team.</em>
            </>
          }
          copy="We’re open to conversations with corporate parks, workplace teams and event organisers across Mumbai."
        />
        <div className="partnership-cards">
          <article>
            <Building2 />
            <p className="eyebrow">Corporate parks</p>
            <h3>A resident everyday café</h3>
            <p>
              Bring Solo Café into your business park as a convenient coffee,
              food and employee-break destination.
            </p>
          </article>
          <article>
            <Store />
            <p className="eyebrow">Office pop-ups</p>
            <h3>Solo, right inside your office</h3>
            <p>
              Host a flexible café pop-up for employee days, launches,
              conferences, team celebrations or recurring office activations.
            </p>
          </article>
        </div>
      </section>

      <section className="contact-layout">
        <div className="contact-copy">
          <p className="eyebrow eyebrow--gold">Start a conversation</p>
          <h2>Tell us what you have in mind.</h2>
          <p>
            Complete the form and we’ll prepare a WhatsApp message for the Solo
            Café team. You can review it before sending.
          </p>
          <div className="contact-direct">
            <strong>Prefer to message directly?</strong>
            <a
              href={whatsappUrl("Hi Solo Café, I’d like to get in touch.")}
              target="_blank"
              rel="noreferrer"
            >
              +91 85919 42655
            </a>
          </div>
        </div>
        <form className="contact-form" onSubmit={submit}>
          <label>
            Your name
            <input
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              minLength={2}
              maxLength={80}
              autoComplete="name"
              required
            />
          </label>
          <label>
            Mobile number
            <input
              value={form.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
            />
          </label>
          <label>
            Work email <span>Optional</span>
            <input
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              type="email"
              autoComplete="email"
            />
          </label>
          <label>
            Company or workplace <span>Optional</span>
            <input
              value={form.company}
              onChange={(event) => updateField("company", event.target.value)}
              autoComplete="organization"
            />
          </label>
          <label className="contact-form__wide">
            What can we help with?
            <select
              value={form.enquiryType}
              onChange={(event) =>
                updateField("enquiryType", event.target.value)
              }
            >
              <option>General enquiry</option>
              <option>Coffee Pass membership</option>
              <option>Corporate park café</option>
              <option>Office pop-up</option>
              <option>Event or team catering</option>
            </select>
          </label>
          <label className="contact-form__wide">
            Your message
            <textarea
              value={form.message}
              onChange={(event) => updateField("message", event.target.value)}
              rows={5}
              minLength={10}
              maxLength={1200}
              required
            />
          </label>
          <div className="contact-form__wide contact-form__submit">
            <button className="button button--red" type="submit">
              Continue on WhatsApp <ArrowRight size={18} />
            </button>
            <p>
              Nothing is sent automatically. WhatsApp opens with your details
              ready for you to review.
            </p>
            {whatsappDraft && (
              <a href={whatsappDraft} target="_blank" rel="noreferrer">
                WhatsApp did not open? Open your prepared message.
              </a>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" updated="10 August 2026">
      <h2>What we collect</h2>
      <p>
        Solo Café collects only the information needed to operate Coffee Club
        memberships: your name, email address, verified mobile or WhatsApp
        number, consent preferences, membership, payment and redemption records,
        and security audit data.
      </p>
      <h2>How we use it</h2>
      <p>
        We use this information to identify your membership, process offline
        payments recorded by staff, maintain balances, prevent duplicate or
        fraudulent redemptions, provide support, and send essential service
        messages you consent to receive.
      </p>
      <h2>Sharing and storage</h2>
      <p>
        Membership data is stored with our configured service providers,
        including Supabase and hosting providers. We do not sell personal
        information. Access is limited by role and logged for security.
      </p>
      <h2>Privacy-conscious website insights</h2>
      <p>
        To improve the website, we count anonymous visits and active, visible
        time spent on each page. This measurement uses a random session ID and
        the page path only. It does not store your name, phone number, IP
        address, query strings, typed content or cross-site advertising
        identifiers. Engagement records are automatically removed after 90 days.
      </p>
      <h2>Your choices</h2>
      <p>
        You may ask to access, correct, or restrict non-essential use of your
        information by contacting Solo Café at +91 85919 42655. Financial,
        redemption and audit records may be retained where reasonably required
        for accounting, security and legal obligations.
      </p>
      <h2>Security</h2>
      <p>
        Redemption QR codes do not contain your phone number or private profile
        details. Short-lived tokens, role-based access and database security
        policies are used to protect the service.
      </p>
    </LegalPage>
  );
}
function TermsPage() {
  return (
    <LegalPage title="Solo Club Terms" updated="23 September 2026">
      <h2>Choose your Solo Club plan</h2>
      <p>
        Solo Club 12 includes 12 coffees for ₹999. Solo Club 20 includes 20
        coffees for ₹1,499. Both plans allow a maximum of three redemptions per
        calendar day.
      </p>
      <p>
        Solo Unlimited costs ₹4,999 and includes unlimited eligible coffees.
        There is no daily quantity limit, but only one coffee may be redeemed at
        a time and at least three hours must pass before the next redemption.
      </p>
      <h2>Coffees included in every plan</h2>
      <p>
        Espresso, Americano, Cappuccino, Latte, Flat White, Iced Americano, Iced
        Latte and Cold Brew are included.
      </p>
      <h2>₹49 Signature upgrade</h2>
      <p>
        For ₹49 per redemption, members may choose Café Mocha, House Cold
        Coffee, Iced Mocha, Vietnamese Cold Brew, Fridge Ciggy, Sunrise
        Americano or Coco-cano. The ₹49 charge applies every time a Signature
        Coffee is selected.
      </p>
      <h2>Not included and add-ons</h2>
      <p>
        Hot Chocolate, Cold Chocolate and Solo Special Hot Chocolate are not
        included in any Solo Club plan. Flavours, oat milk, almond milk, coconut
        milk, other alternative milks and all customisations are charged
        separately at the regular menu price, even when the base coffee is
        included.
      </p>
      <h2>Validity and unused coffees</h2>
      <p>
        Every plan is valid for 30 calendar days from staff activation. Unused
        coffees on Solo Club 12 and Solo Club 20 expire at the end of the
        validity period. They do not roll over and have no refund, transfer or
        cash value. Expired memberships cannot be redeemed.
      </p>
      <h2>Personal use and redemptions</h2>
      <p>
        Memberships are personal, non-transferable and cannot be shared. The
        member must be physically present when redeeming. Each credit or
        redemption covers one drink only. Unlimited members cannot redeem for
        colleagues, friends or other people.
      </p>
      <p>
        The plan begins only after Solo staff verifies and records payment. QR
        codes are short-lived and cannot be reused. Solo may pause access while
        investigating misuse, duplicate scans or account security issues. Staff
        may reverse an incorrect redemption, with the correction recorded in the
        audit history.
      </p>
    </LegalPage>
  );
}
function LegalPage({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <div className="legal-page">
      <p className="eyebrow">Solo Café</p>
      <h1>{title}</h1>
      <p className="updated">Last updated {updated}</p>
      <div className="legal-copy">{children}</div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="not-found">
      <span>404</span>
      <h1>This break went off-menu.</h1>
      <NavLink className="button button--red" to="/">
        Back to Solo
      </NavLink>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(!isSupabaseConfigured);
  const [membershipCheck, setMembershipCheck] = useState<MembershipCheck>(null);
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setAuthReady(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if (!supabase || !session) return;
    let cancelled = false;
    void supabase
      .from("memberships")
      .select("id")
      .eq("profile_id", session.user.id)
      .in("status", ["active", "pending", "paused"])
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        setMembershipCheck({
          userId: session.user.id,
          hasCurrent: error ? true : Boolean(data),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [session]);
  const { hasCurrentMembership: memberExperience, showJoinPrompt } =
    resolveMembershipUi(authReady, session?.user.id || null, membershipCheck);
  return (
    <Layout
      session={session}
      hasCurrentMembership={memberExperience}
      showJoinPrompt={showJoinPrompt}
    >
      <EngagementTracker />
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              hasCurrentMembership={memberExperience}
              showJoinPrompt={showJoinPrompt}
            />
          }
        />
        <Route path="/menu" element={<MenuPage />} />
        <Route
          path="/membership"
          element={
            <MembershipPage
              hasCurrentMembership={memberExperience}
              showJoinPrompt={showJoinPrompt}
            />
          }
        />
        <Route path="/join" element={<JoinPage session={session} />} />
        <Route
          path="/dashboard"
          element={<MemberDashboard session={session} />}
        />
        <Route path="/staff" element={<StaffAccessPage session={session} />} />
        <Route path="/admin" element={<AdminAccessPage session={session} />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/solo-at-work" element={<SoloAtWorkPage />} />
        <Route path="/visit" element={<VisitPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}
